<template>
    <v-container :fluid="true" class="max-height">
        <v-row>
            <v-col class="px-5 d-flex justify-space-between align-center" cols="12">
                <h1 class="text-left">Asset Details</h1>
                <div class="d-flex align-center ga-2">
                    <NoteIcon v-if="assetDetails.id" entity-type="assets" :entity-id="assetDetails.id"
                              :pinned-note="assetDetails.pinned_note?.note"
                              :note-count="assetDetails.notes_count ?? 0" size="small" :icon-size="20"/>
                    <v-btn color="warning" prepend-icon="mdi-pencil" size="small" @click="editDialogSwitch = true">
                        Edit
                    </v-btn>
                </div>
            </v-col>
        </v-row>

        <v-row>
            <v-col cols="12" md="9">
                <v-table density="compact" hover>
                    <tbody class="text-start">
                    <tr>
                        <td class="font-weight-bold">Name</td>
                        <td>
                            <div class="d-flex align-center ga-2 flex-wrap">
                                <span>{{ assetDetails.label }}</span>
                                <template v-if="assetDetails.fleet_status">
                                    <v-chip :color="fleetStatusBadge.color"
                                            :prepend-icon="fleetStatusBadge.icon"
                                            :variant="fleetStatusBadge.variant || 'tonal'"
                                            size="x-small" density="comfortable">
                                        {{ fleetStatusBadge.label }}
                                    </v-chip>
                                    <template v-if="!isAssetHealthy">
                                        <v-tooltip v-if="isAssetOffline" text="Non-Reporting" location="top">
                                            <template #activator="{ props: tp }">
                                                <v-icon v-bind="tp" icon="mdi-signal-off" color="#d70000" size="x-small"/>
                                            </template>
                                        </v-tooltip>
                                        <template v-else>
                                            <v-icon v-bind="signalIcon(assetDetails.signal_bars ?? 0)" size="x-small"/>
                                            <v-icon v-bind="batteryIcon(assetDetails.battery_bars ?? 0, primaryBatteryType(assetDetails, assetDetails.device?.product_sku))" size="x-small"/>
                                        </template>
                                    </template>
                                    <v-tooltip v-if="assetDetails.fleet_status && isAssetHealthy" text="All OK" location="top">
                                        <template #activator="{ props: tp }">
                                            <v-icon v-bind="tp" icon="mdi-check-circle" color="green" size="x-small"/>
                                        </template>
                                    </v-tooltip>
                                </template>
                                <v-tooltip v-if="catConfig.showDtc && assetDetails.obd_present && (assetDetails.alert_lamb_status || assetDetails.obd_pending_dtcs || assetDetails.obd_permanent_dtcs || j1939LampLit(assetDetails))"
                                           location="top" :text="engineCheckTab === 7 ? 'Engine Check - Click to view vehicle health' : 'Engine Check - Click to view DTCs'">
                                    <template v-slot:activator="{ props }">
                                        <v-btn :aria-label="engineCheckTab === 7 ? 'Engine check, view vehicle health' : 'Engine check, view diagnostic codes'"
                                               color="warning" icon="mdi-engine-outline" size="32" v-bind="props"
                                               @click="openEngineCheck"/>
                                    </template>
                                </v-tooltip>
                                <!-- >= 0, not > 0: the firmware sends 0xFF when the car does not
                                     answer and the gateway drops it, so a stored 0 is a real
                                     reading of an empty tank - the one worth showing. Capped at
                                     100 (a percent) rather than 255, so an older row holding a
                                     raw byte still cannot render. -->
                                <v-chip v-if="catConfig.showFuel && assetDetails.device_status?.fuel_level != null && assetDetails.device_status.fuel_level >= 0 && assetDetails.device_status.fuel_level <= 100"
                                        size="x-small" density="comfortable" variant="tonal"
                                        :color="assetDetails.device_status.fuel_level < 20 ? 'red' : assetDetails.device_status.fuel_level <= 30 ? 'orange' : 'teal'"
                                        prepend-icon="mdi-gas-station">
                                    {{ assetDetails.device_status.fuel_level }}%
                                </v-chip>
                            </div>
                        </td>
                        <td class="font-weight-bold">Device</td>
                        <td>
                            <a v-if="asset?.device?.slno"
                               class="text-primary cursor-pointer text-decoration-none"
                               @click="navigateTo('device', {slno: asset.device.slno})">{{ asset.device.slno }}</a>
                            <span v-else class="text-grey">No device assigned</span>
                            <v-btn v-if="assetDetails.slno" icon size="x-small" variant="text"
                                   density="compact" class="ms-1"
                                   @click="showDeviceInfo = !showDeviceInfo">
                                <v-icon :icon="showDeviceInfo ? 'mdi-chevron-up' : 'mdi-chevron-down'" size="small"/>
                            </v-btn>
                            <v-avatar v-if="assetDetails.asset_type?.photo" :image="assetDetails.asset_type.photo"
                                      class="ms-2"
                                      rounded="sm" variant="elevated"/>
                            <v-avatar v-if="assetDetails.photo" :image="assetDetails.photo" class="ms-2"
                                      rounded="sm" variant="elevated"/>
                            <UserAvatar v-if="assetDetails.driver_name" :src="assetDetails.driver_photo"
                                        :name="assetDetails.driver_name" class="ms-2" rounded="sm"/>
                        </td>
                    </tr>
                    <tr v-for="row in asset_table">
                        <template v-for="col in row">
                            <td class="font-weight-bold">{{ col.title }}</td>
                            <td v-if="col.type === 'date'">
                                {{
                                    getObjVal(assetDetails, col.key) ? (new Date(getObjVal(assetDetails, col.key)).toLocaleDateString()) : ''
                                }}
                            </td>
                            <td v-else-if="col.type === 'datetime'">
                                {{
                                    getObjVal(assetDetails, col.key) ? formatDateTime(getObjVal(assetDetails, col.key)) : ''
                                }}
                            </td>
                            <td v-else-if="col.type === 'distance'">
                                {{ getObjVal(assetDetails, col.key) ? strOperations().unitifyDistance(getObjVal(assetDetails, col.key), 'distance', col.baseUnit || 'miles') : '' }}
                            </td>
                            <td v-else-if="col.key === 'vin' || col.key === 'acquired_vin'">
                                <template v-if="col.key === 'acquired_vin' && getObjVal(assetDetails, col.key)">
                                    {{ getObjVal(assetDetails, col.key) ?? '' }} ({{
                                        getObjVal(assetDetails, 'bus_type_name') ?? ''
                                    }})
                                </template>
                                <template v-else>
                                    {{ getObjVal(assetDetails, col.key) ?? '' }}
                                </template>
                                <v-icon class="mdi mdi-content-copy cursor-pointer ms-2"
                                        v-if="getObjVal(assetDetails, col.key)??false"
                                        @click="copyToClipboard(getObjVal(assetDetails, col.key) ?? '')"/>
                            </td>
                            <td v-else>{{ getObjVal(assetDetails, col.key) || (col.fallbackKey ? getObjVal(assetDetails, col.fallbackKey) : '') || '' }}</td>
                        </template>
                    </tr>
                    <tr v-if="asset">
                        <td class="font-weight-bold">Monitoring Configs</td>
                        <td>
                            <v-chip-group>
                                <template v-for="(config, index) in (asset.monitoring_configs || [])" :key="index">
                                    <v-chip size="x-small" style="max-width: 160px;">
                                        <span class="me-1 text-truncate">{{ config.name }}</span>
                                        <v-icon icon="mdi-close-circle"
                                                @click="monitoringConfigDetach(config)"/>
                                    </v-chip>
                                </template>
                                <v-btn color="green" density="compact" flat icon="mdi-plus" size="small"
                                       @click="openMonitoringConfigDialog"/>
                            </v-chip-group>
                        </td>
                        <td class="font-weight-bold">Asset Groups</td>
                        <td>
                            <v-chip-group>
                                <template v-for="(group, index) in (asset.asset_groups || [])" :key="index">
                                    <v-chip size="x-small" style="max-width: 160px;">
                                        <span class="me-1 text-truncate">{{ group.name }}</span>
                                        <v-icon v-if="group.type > 1"
                                                icon="mdi-close-circle"
                                                @click="assetGroupDetach(group)"/>
                                    </v-chip>
                                </template>
                                <v-btn color="green" density="compact" flat icon="mdi-plus" size="small"
                                       @click="openAssetGroupDialog"/>
                            </v-chip-group>
                        </td>
                    </tr>
                    </tbody>
                </v-table>
                <!-- Device & Connectivity Info (toggled by chevron next to serial number) -->
                <v-expand-transition>
                    <div v-if="showDeviceInfo && assetDetails.slno" class="pa-2" style="border: 1.5px solid #90CAF9; border-radius: 8px; margin-top: 4px; background-color: #F5F9FF;">
                            <v-row dense>
                                <!-- Device Hardware -->
                                <v-col cols="12" md="6">
                                    <div class="text-caption font-weight-bold text-grey mb-1">DEVICE</div>
                                    <v-table density="compact">
                                        <tbody class="text-start">
                                        <tr>
                                            <td class="text-grey" style="width:140px">IMEI</td>
                                            <td>{{ assetDetails.imei || '-' }}
                                                <v-icon v-if="assetDetails.imei" class="mdi mdi-content-copy cursor-pointer ms-1" size="x-small"
                                                        @click="copyToClipboard(assetDetails.imei)"/>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td class="text-grey">Serial</td>
                                            <td>{{ assetDetails.slno || '-' }}</td>
                                        </tr>
                                        <tr>
                                            <td class="text-grey">Product</td>
                                            <td>{{ assetDetails.product_name || '-' }}</td>
                                        </tr>
                                        <tr v-if="assetDetails.hw_revision">
                                            <td class="text-grey">HW Revision</td>
                                            <td>{{ assetDetails.hw_revision }}</td>
                                        </tr>
                                        </tbody>
                                    </v-table>
                                </v-col>

                                <!-- Firmware & OTA -->
                                <v-col cols="12" md="6">
                                    <div class="text-caption font-weight-bold text-grey mb-1">FIRMWARE</div>
                                    <v-table density="compact">
                                        <tbody class="text-start">
                                        <tr v-if="assetDetails.firmware_app">
                                            <td class="text-grey" style="width:140px">App</td>
                                            <td>{{ assetDetails.firmware_app }}</td>
                                        </tr>
                                        <tr v-if="assetDetails.firmware_modem">
                                            <td class="text-grey">Modem</td>
                                            <td>{{ assetDetails.firmware_modem }}</td>
                                        </tr>
                                        <tr v-if="assetDetails.firmware_bt">
                                            <td class="text-grey">Bluetooth</td>
                                            <td>{{ assetDetails.firmware_bt }}</td>
                                        </tr>
                                        <tr v-if="assetDetails.ota_status != null && assetDetails.ota_status > 0">
                                            <td class="text-grey">OTA</td>
                                            <td>
                                                <v-chip size="x-small" :color="otaStatusColor(assetDetails.ota_status)">
                                                    {{ otaStatusLabel(assetDetails.ota_status) }}
                                                </v-chip>
                                                <span v-if="assetDetails.ota_progress > 0" class="ms-1 text-caption">
                                                    {{ assetDetails.ota_progress }}%
                                                </span>
                                            </td>
                                        </tr>
                                        </tbody>
                                    </v-table>
                                </v-col>

                                <!-- Signal & GPS Quality -->
                                <v-col cols="12" md="6">
                                    <div class="text-caption font-weight-bold text-grey mb-1">CONNECTIVITY</div>
                                    <v-table density="compact">
                                        <tbody class="text-start">
                                        <tr>
                                            <td class="text-grey" style="width:140px">Signal</td>
                                            <td>
                                                <v-icon v-bind="signalIcon(assetDetails.signal_bars ?? 0)" size="small"/>
                                                <span class="ms-1">{{ assetDetails.signal_bars ?? 0 }} bars</span>
                                                <span v-if="assetDetails.rsrp != null" class="text-caption text-grey ms-2">
                                                    RSRP {{ assetDetails.rsrp }} dBm
                                                </span>
                                            </td>
                                        </tr>
                                        <tr v-if="assetDetails.rssi != null">
                                            <td class="text-grey">RSSI</td>
                                            <td>{{ assetDetails.rssi }} dBm
                                                <span v-if="assetDetails.rsrq != null" class="text-caption text-grey ms-2">
                                                    RSRQ {{ assetDetails.rsrq }} dB
                                                </span>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td class="text-grey">GPS</td>
                                            <td>
                                                <template v-if="assetDetails.satellites != null">
                                                    {{ assetDetails.satellites }} sats,
                                                    {{ assetDetails.fix_type === 3 ? '3D' : assetDetails.fix_type === 2 ? '2D' : 'No' }} fix
                                                    <span v-if="assetDetails.gps_accuracy != null" class="text-caption text-grey ms-1">
                                                        ±{{ assetDetails.gps_accuracy }}m
                                                    </span>
                                                </template>
                                                <span v-else class="text-grey">-</span>
                                            </td>
                                        </tr>
                                        <tr v-if="assetDetails.sim_iccid">
                                            <td class="text-grey">SIM</td>
                                            <td>
                                                {{ assetDetails.sim_type === 'E' ? 'eSIM' : 'SIM' }}
                                                <span class="text-caption text-grey ms-1">
                                                    ...{{ assetDetails.sim_iccid?.slice(-6) }}
                                                </span>
                                            </td>
                                        </tr>
                                        </tbody>
                                    </v-table>
                                </v-col>

                                <!-- Sensors -->
                                <v-col cols="12" md="6">
                                    <div class="text-caption font-weight-bold text-grey mb-1">SENSORS</div>
                                    <v-table density="compact">
                                        <tbody class="text-start">
                                        <tr>
                                            <td class="text-grey" style="width:140px">Battery</td>
                                            <td>
                                                <v-icon v-bind="batteryIcon(assetDetails.battery_bars ?? 0, primaryBatteryType(assetDetails, assetDetails.device?.product_sku))" size="small"/>
                                                <span class="ms-1">{{ assetDetails.battery_bars ?? 0 }} bars</span>
                                                <span v-if="primaryVoltageMv > 0" class="text-caption text-grey ms-2">
                                                    {{ (primaryVoltageMv / 1000).toFixed(1) }}V
                                                </span>
                                            </td>
                                        </tr>
                                        <tr v-if="assetDetails.temperature_onboard != null">
                                            <td class="text-grey">Device Temp</td>
                                            <td>{{ assetDetails.temperature_onboard }}°C</td>
                                        </tr>
                                        <!-- Same range rule as the chip above: an empty tank is a
                                             real reading, a raw byte is not. -->
                                        <tr v-if="catConfig.showFuel && assetDetails.device_status?.fuel_level != null && assetDetails.device_status.fuel_level >= 0 && assetDetails.device_status.fuel_level <= 100">
                                            <td class="text-grey">Fuel Level</td>
                                            <td>{{ assetDetails.device_status.fuel_level }}%</td>
                                        </tr>
                                        <tr v-if="assetDetails.vehicle_odometer != null">
                                            <td class="text-grey">OBD Odometer</td>
                                            <td>{{ strOperations().unitifyDistance(assetDetails.vehicle_odometer, 'odometer', 'km') }}</td>
                                        </tr>
                                        </tbody>
                                    </v-table>
                                </v-col>

                                <!-- OBD Mount Orientation 3D View -->
                                <v-col v-if="assetDetails.mount_roll != null" cols="12">
                                    <div class="text-caption font-weight-bold text-grey mb-1">OBD MOUNT ORIENTATION</div>
                                    <div class="d-flex align-center ga-4">
                                        <div class="mount-3d-container" style="width: 200px; height: 200px; perspective: 600px;">
                                            <div class="mount-3d-scene" style="width: 100%; height: 100%; position: relative; transform-style: preserve-3d; transform: rotateX(-25deg) rotateY(35deg);">
                                                <!-- Vehicle axes (blue) -->
                                                <svg viewBox="-100 -100 200 200" style="position: absolute; width: 100%; height: 100%;">
                                                    <!-- X axis (forward) - blue -->
                                                    <line x1="0" y1="0" x2="80" y2="0" stroke="#1976D2" stroke-width="2"/>
                                                    <polygon points="80,-5 90,0 80,5" fill="#1976D2"/>
                                                    <text x="92" y="4" fill="#1976D2" font-size="11" font-weight="bold">X</text>
                                                    <!-- Y axis (left) - blue -->
                                                    <line x1="0" y1="0" x2="0" y2="-80" stroke="#1976D2" stroke-width="2"/>
                                                    <polygon points="-5,-80 0,-90 5,-80" fill="#1976D2"/>
                                                    <text x="4" y="-85" fill="#1976D2" font-size="11" font-weight="bold">Y</text>
                                                    <!-- Z axis (up) - blue dashed (projected) -->
                                                    <line x1="0" y1="0" x2="-50" y2="50" stroke="#1976D2" stroke-width="1.5" stroke-dasharray="4,3"/>
                                                    <text x="-60" y="60" fill="#1976D2" font-size="11" font-weight="bold">Z</text>
                                                    <!-- Vehicle box outline -->
                                                    <rect x="-15" y="-8" width="55" height="16" rx="3" fill="none" stroke="#1976D2" stroke-width="1" opacity="0.3"/>
                                                    <text x="-12" y="28" fill="#90CAF9" font-size="9">Vehicle</text>

                                                    <!-- OBD axes (red) - rotated by mount angles -->
                                                    <g :transform="`rotate(${-(assetDetails.mount_yaw || 0)})`">
                                                        <g :transform="`skewX(${(assetDetails.mount_pitch || 0) * 0.5})`">
                                                            <!-- OBD X axis - red -->
                                                            <line x1="0" y1="0" x2="60" y2="0" stroke="#E53935" stroke-width="2"/>
                                                            <polygon points="60,-4 68,0 60,4" fill="#E53935"/>
                                                            <text x="70" y="4" fill="#E53935" font-size="10">x</text>
                                                            <!-- OBD Y axis - red -->
                                                            <line x1="0" y1="0" x2="0" y2="-60" stroke="#E53935" stroke-width="2"/>
                                                            <polygon points="-4,-60 0,-68 4,-60" fill="#E53935"/>
                                                            <text x="4" y="-62" fill="#E53935" font-size="10">y</text>
                                                            <!-- OBD Z axis - red dashed -->
                                                            <line x1="0" y1="0" x2="-40" y2="40" stroke="#E53935" stroke-width="1.5" stroke-dasharray="4,3"/>
                                                            <text x="-48" y="48" fill="#E53935" font-size="10">z</text>
                                                        </g>
                                                    </g>

                                                    <!-- Origin dot -->
                                                    <circle cx="0" cy="0" r="3" fill="#333"/>
                                                </svg>
                                            </div>
                                        </div>
                                        <div>
                                            <v-table density="compact" class="text-caption">
                                                <tbody>
                                                <tr>
                                                    <td class="text-grey" style="width:60px">Roll</td>
                                                    <td class="font-weight-medium">{{ assetDetails.mount_roll }}°</td>
                                                </tr>
                                                <tr>
                                                    <td class="text-grey">Pitch</td>
                                                    <td class="font-weight-medium">{{ assetDetails.mount_pitch }}°</td>
                                                </tr>
                                                <tr>
                                                    <td class="text-grey">Yaw</td>
                                                    <td class="font-weight-medium">{{ assetDetails.mount_yaw }}°</td>
                                                </tr>
                                                </tbody>
                                            </v-table>
                                            <div class="text-caption mt-1">
                                                <span style="color: #1976D2;">&#9644; Vehicle</span>
                                                <span class="ms-2" style="color: #E53935;">&#9644; OBD Sensor</span>
                                            </div>
                                        </div>
                                    </div>
                                </v-col>
                            </v-row>
                    </div>
                </v-expand-transition>
            </v-col>

            <v-col v-if="assetDetails.device_status?.latitude && assetDetails.device_status?.longitude"
                   cols="12" md="3">
                <div class="asset-location-map" style="height: 220px; position: relative; border-radius: 8px; overflow: hidden; border: 1px solid #e0e0e0;">
                    <l-map ref="locationMap" :zoom="14"
                           :center="[assetDetails.device_status.latitude, assetDetails.device_status.longitude]"
                           :options="{zoomControl: false, attributionControl: false}"
                           @ready="onLocationMapReady">
                        <l-tile-layer :url="$mapTiles.street"/>
                        <l-marker :lat-lng="[assetDetails.device_status.latitude, assetDetails.device_status.longitude]">
                            <l-icon :icon-size="[30, 30]">
                                <i class="mdi mdi-map-marker" :style="{fontSize: '30px', color: asset?.color ?? '#1976D2'}"></i>
                            </l-icon>
                        </l-marker>
                    </l-map>
                    <div style="position: absolute; bottom: 8px; right: 8px; z-index: 1000;" class="d-flex ga-1">
                        <v-tooltip text="Copy Location" location="top">
                            <template #activator="{ props: tp }">
                                <v-btn v-bind="tp" icon="mdi-content-copy" size="x-small" color="white"
                                       variant="elevated" density="compact"
                                       @click="copyToClipboard(`https://www.google.com/maps?q=${assetDetails.device_status.latitude},${assetDetails.device_status.longitude}`)"/>
                            </template>
                        </v-tooltip>
                        <v-tooltip text="Open in Google Maps" location="top">
                            <template #activator="{ props: tp }">
                                <v-btn v-bind="tp" icon="mdi-google-maps" size="x-small" color="white"
                                       variant="elevated" density="compact"
                                       @click="openGoogleMaps"/>
                            </template>
                        </v-tooltip>
                    </div>
                </div>
                <div class="text-caption text-center mt-1 text-grey">
                    {{ assetDetails.device_status.latitude }}, {{ assetDetails.device_status.longitude }}
                </div>
            </v-col>
        </v-row>

        <v-row v-if="dvirOutOfService">
            <v-col cols="12" class="pb-0">
                <v-alert type="error" variant="tonal" density="compact" icon="mdi-cancel" prominent>
                    <div class="d-flex align-center justify-space-between flex-wrap ga-2">
                        <span class="font-weight-medium">
                            Out of service - this vehicle has an open critical safety defect and should not be
                            dispatched until it is repaired.
                        </span>
                        <v-btn size="small" variant="flat" color="error" @click="activeTab = 6; updateTab()">
                            Review defects
                        </v-btn>
                    </div>
                </v-alert>
            </v-col>
        </v-row>

        <v-row>
            <v-col class="mb-5" cols="12">
                <v-tabs v-model="activeTab" grow @update:model-value="updateTab">
                    <v-tab
                        v-for="tab in tabs"
                        :key="tab.value"
                        :value="tab.value"
                        :loading="!asset">
                        <template v-slot:default>
                            {{ tab.title }}
                            <MenuCountChip v-if="tabBadges[tab.value]" :badge="tabBadges[tab.value]"/>
                        </template>
                    </v-tab>
                </v-tabs>

                <v-tabs-window v-model="activeTab">
                    <v-tabs-window-item
                        v-for="tab in tabs"
                        :key="tab.value"
                        :value="tab.value">
                        <v-card flat>
                            <v-card-text v-if="activeTab === 6">
                                <div class="d-flex align-center justify-space-between mb-2">
                                    <h3 class="text-subtitle-1 font-weight-bold">Open Defects</h3>
                                    <v-btn icon="mdi-refresh" size="x-small" variant="text"
                                           :loading="dvirProcessing" @click="fetchDvir"/>
                                </div>
                                <v-table density="compact" hover class="mb-6">
                                    <thead>
                                    <tr>
                                        <th>Reported</th>
                                        <th>Defect</th>
                                        <th class="text-center">Severity</th>
                                        <th class="text-center">Status</th>
                                        <th class="text-center">Action</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    <tr v-for="d in dvirDefects" :key="d.id" class="text-left">
                                        <td>{{ formatDateTime(d.created_at) }}</td>
                                        <td>
                                            {{ d.label }}
                                            <v-btn v-if="d.photo_path" size="x-small" variant="text" icon="mdi-image"
                                                   @click="viewDvirPhoto(d.photo_path)"/>
                                            <div v-if="d.note" class="text-caption text-grey">{{ d.note }}</div>
                                        </td>
                                        <td class="text-center">
                                            <v-chip size="x-small" variant="flat" :color="sevColor(d.severity)">
                                                {{ d.severity }}
                                            </v-chip>
                                        </td>
                                        <td class="text-center">
                                            <v-chip size="x-small" variant="tonal" :color="defectStatusColor(d.status)">
                                                {{ d.status }}
                                            </v-chip>
                                            <div v-if="d.service_task_id" class="text-caption text-grey">
                                                work order #{{ d.service_task_id }}
                                            </div>
                                        </td>
                                        <td class="text-center">
                                            <v-btn v-if="!d.service_task_id" size="x-small" variant="tonal"
                                                   color="primary" :loading="dvirBusy === d.id"
                                                   @click="dvirCreateWorkOrder(d)">Work order
                                            </v-btn>
                                            <v-btn v-if="d.status !== 'resolved'" size="x-small" variant="text"
                                                   color="success" :disabled="dvirBusy === d.id"
                                                   @click="dvirResolveDefect(d)">Resolve
                                            </v-btn>
                                        </td>
                                    </tr>
                                    <tr v-if="!dvirDefects.length">
                                        <td colspan="5" class="text-center text-grey py-3">
                                            <v-progress-circular v-if="dvirProcessing" color="primary" indeterminate
                                                                 size="20"/>
                                            <span v-else>No open defects</span>
                                        </td>
                                    </tr>
                                    </tbody>
                                </v-table>

                                <h3 class="text-subtitle-1 font-weight-bold mb-2">Recent Inspections</h3>
                                <v-table density="compact" hover>
                                    <thead>
                                    <tr>
                                        <th>Date</th>
                                        <th>Driver</th>
                                        <th class="text-center">Type</th>
                                        <th class="text-center">Status</th>
                                        <th class="text-center">Location</th>
                                        <th class="text-center">Open defects</th>
                                        <th>Completed scheduled</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    <tr v-for="ins in dvirInspections" :key="ins.id" class="text-left">
                                        <td>{{ formatDateTime(ins.submitted_at) }}</td>
                                        <td>{{ dvirDriverName(ins.driver) }}</td>
                                        <td class="text-center text-uppercase">{{ ins.type }}</td>
                                        <td class="text-center">
                                            <v-chip size="x-small" variant="flat" :color="dvirStatusColor(ins.status)">
                                                {{ ins.status }}
                                            </v-chip>
                                        </td>
                                        <td class="text-center">
                                            <v-icon :color="ins.gps_verified ? 'success' : 'grey'" size="18">
                                                {{ ins.gps_verified ? 'mdi-map-marker-check' : 'mdi-map-marker-off' }}
                                            </v-icon>
                                        </td>
                                        <td class="text-center">
                                            <v-chip v-if="ins.open_defects_count" size="x-small" color="error"
                                                    variant="tonal">{{ ins.open_defects_count }}
                                            </v-chip>
                                            <span v-else>-</span>
                                        </td>
                                        <td>
                                            <span v-if="ins.scheduled_items?.length" class="text-caption">
                                                {{ ins.scheduled_items.map(i => i.vehicle_service_type?.name).filter(Boolean).join(', ') }}
                                            </span>
                                            <span v-else>-</span>
                                        </td>
                                    </tr>
                                    <tr v-if="!dvirInspections.length">
                                        <td colspan="7" class="text-center text-grey py-3">
                                            <v-progress-circular v-if="dvirProcessing" color="primary" indeterminate
                                                                 size="20"/>
                                            <span v-else>No inspections yet</span>
                                        </td>
                                    </tr>
                                    </tbody>
                                </v-table>
                                <v-pagination v-if="dvirInspectionsTotalPages > 1" v-model="dvirInspectionsPage"
                                              :length="dvirInspectionsTotalPages" class="py-0 pt-2" density="compact"
                                              @update:model-value="fetchDvirInspections"/>
                            </v-card-text>

                            <v-card-text v-if="activeTab === 0">
                                <v-row class="mb-5">
                                    <v-col class="text-start">
                                        <v-btn color="black" size="small" @click="showCreateTaskDialog(1)">Schedule
                                            Inspection
                                        </v-btn>
                                    </v-col>
                                </v-row>

                                <v-row>
                                    <v-col cols="12">
                                        <v-table density="compact" hover>
                                            <thead>
                                            <tr>
                                                <th>Label</th>
                                                <th>Created</th>
                                                <th>Due</th>
                                                <th>Repeats</th>
                                                <th>Status</th>
                                                <th>Action</th>
                                            </tr>
                                            </thead>
                                            <tbody>
                                            <tr v-for="(inspectionTask, index) in inspectionTasks"
                                                v-if="!inspectionsProcessing && inspectionTasks.length" :key="index"
                                                class="text-left">
                                                <td>{{ inspectionTask.label }}</td>
                                                <td>{{
                                                        formatDateTime(inspectionTask.created_at)
                                                    }}
                                                </td>
                                                <td>{{ inspectionTask.due_date ? String(inspectionTask.due_date).slice(0, 10) : '-' }}</td>
                                                <td>{{ inspectionTask.repeat_days ? `Every ${inspectionTask.repeat_days} days` : 'Once' }}</td>
                                                <td>
                                                    <v-chip :color="statusColor(inspectionTask.status)"
                                                            density="comfortable"
                                                            size="small">
                                                        {{ inspectionTask.status_title }}
                                                    </v-chip>
                                                    <v-chip v-if="inspectionTask.driver_inspection_id && inspectionTask.status === 3" class="ms-1"
                                                            color="success" density="comfortable" size="small"
                                                            variant="tonal">
                                                        By driver DVIR
                                                    </v-chip>
                                                </td>
                                                <td>
                                                    <v-btn size="x-small" variant="outlined"
                                                           @click="showTaskDetailsDialog(inspectionTask)">View
                                                    </v-btn>
                                                </td>
                                            </tr>
                                            <tr v-else>
                                                <td v-if="inspectionsProcessing" colspan="6">
                                                    <v-progress-circular color="primary" indeterminate/>
                                                </td>
                                                <td v-else colspan="6">No scheduled inspections</td>
                                            </tr>
                                            </tbody>
                                        </v-table>

                                        <v-pagination v-if="inspectionTasksTotalPages > 1"
                                                      v-model="inspectionTasksCurrentPage"
                                                      :length="inspectionTasksTotalPages"
                                                      class="py-0 pt-2"
                                                      density="compact"
                                                      @click="fetchInspectionTasks"/>
                                    </v-col>
                                </v-row>
                            </v-card-text>

                            <v-card-text v-if="activeTab === 1">
                                <v-row class="mb-5">
                                    <v-col class="text-start">
                                        <v-btn color="black" size="small" @click="showCreateTaskDialog(0)">Create
                                            Maintenance Order
                                        </v-btn>
                                    </v-col>
                                </v-row>

                                <v-row>
                                    <v-col cols="12">
                                        <v-table density="compact" hover>
                                            <thead>
                                            <tr>
                                                <th>Label</th>
                                                <th>Timestamp</th>
                                                <th>Due</th>
                                                <th>Status</th>
                                                <th>Action</th>
                                            </tr>
                                            </thead>
                                            <tbody>
                                            <tr v-for="(maintenanceTask, index) in maintenanceTasks"
                                                v-if="!maintenancesProcessing && maintenanceTasks.length" :key="index"
                                                class="text-left">
                                                <td>
                                                    {{ maintenanceTask.label }}
                                                    <v-chip v-if="maintenanceTask.inspection_defect"
                                                            size="x-small" variant="tonal" color="info" class="ml-1"
                                                            prepend-icon="mdi-clipboard-check-outline">
                                                        From inspection
                                                        <v-tooltip activator="parent" location="top">
                                                            Raised from a driver inspection defect:
                                                            {{ maintenanceTask.inspection_defect.label }}
                                                            ({{ maintenanceTask.inspection_defect.severity }})
                                                        </v-tooltip>
                                                    </v-chip>
                                                </td>
                                                <td>{{
                                                        formatDateTime(maintenanceTask.created_at)
                                                    }}
                                                </td>
                                                <td>{{ maintenanceTask.due_date ? String(maintenanceTask.due_date).slice(0, 10) : '-' }}</td>
                                                <td>
                                                    <v-chip :color="statusColor(maintenanceTask.status)"
                                                            density="comfortable"
                                                            size="small">
                                                        {{ maintenanceTask.status_title }}
                                                    </v-chip>
                                                </td>
                                                <td>
                                                    <v-btn size="x-small" variant="outlined"
                                                           @click="showTaskDetailsDialog(maintenanceTask)">View
                                                    </v-btn>
                                                </td>
                                            </tr>
                                            <tr v-else>
                                                <td v-if="maintenancesProcessing" colspan="5">
                                                    <v-progress-circular color="primary" indeterminate/>
                                                </td>
                                                <td v-else colspan="5">No maintenance task found</td>
                                            </tr>
                                            </tbody>
                                        </v-table>

                                        <v-pagination v-if="maintenanceTasksTotalPages > 1"
                                                      v-model="maintenanceTasksCurrentPage"
                                                      :length="maintenanceTasksTotalPages"
                                                      class="py-0 pt-2"
                                                      density="compact"
                                                      @click="fetchMaintenanceTasks"/>
                                    </v-col>
                                </v-row>
                            </v-card-text>

                            <v-card-text v-if="activeTab === 2">
                                <v-row class="mb-5">
                                    <v-col class="text-start">
                                        <v-btn color="black" size="small" @click="showCreateFuelLogDialog">Add Entry
                                        </v-btn>
                                    </v-col>
                                </v-row>

                                <v-row>
                                    <v-col cols="12">
                                        <v-table density="compact" hover>
                                            <thead>
                                            <tr>
                                                <th>Fuel Type</th>
                                                <th>Vendor/ Location</th>
                                                <th>Payment Type</th>
                                                <th>Card Last 5</th>
                                                <th>Odometer</th>
                                                <th>Qty</th>
                                                <th>Unit Cost</th>
                                                <th>Tax</th>
                                                <th>Total Cost</th>
                                                <th>Timestamp</th>
                                                <th>Action</th>
                                            </tr>
                                            </thead>
                                            <tbody>
                                            <tr v-for="(fuelLog, index) in fuelLogs"
                                                v-if="!fuelLogsProcessing && fuelLogs.length" :key="index"
                                                class="text-left">
                                                <td>{{ getFuelType(fuelLog.vehicle_fuel_type_id)?.name ?? '' }}</td>
                                                <td>{{ fuelLog.vendor }}</td>
                                                <td>{{
                                                        getPaymentType(fuelLog.driver_payment_type_id)?.name ?? ''
                                                    }}
                                                </td>
                                                <td>{{
                                                        getPaymentType(fuelLog.driver_payment_type_id)?.last5 ?? ''
                                                    }}
                                                </td>
                                                <td>{{ fuelLog.odometer ? strOperations().unitifyDistance(fuelLog.odometer, 'distance', 'km') : '' }}</td>
                                                <td>{{ fuelLog.quantity }}</td>
                                                <td>{{ fuelLog.cost_per_unit }}</td>
                                                <td>{{ fuelLog.tax }}</td>
                                                <td>{{ fuelLog.total_cost }}</td>
                                                <td>{{ formatDateTime(fuelLog.created_at) }}</td>
                                                <td>
                                                    <v-btn color="black" size="x-small" variant="outlined"
                                                           @click="showCreateFuelLogDialog(fuelLog)">Edit
                                                    </v-btn>
                                                    <ReceiptButton v-if="mayHaveReceipt(fuelLog)" :endpoint="`fleet/fuel-logs/${fuelLog.id}/receipt`"/>
                                                </td>
                                            </tr>
                                            <tr v-else>
                                                <td v-if="fuelLogsProcessing" colspan="10">
                                                    <v-progress-circular color="primary" indeterminate/>
                                                </td>
                                                <td v-else colspan="10">No fuel entries found</td>
                                            </tr>
                                            </tbody>
                                        </v-table>

                                        <v-pagination v-if="fuelLogsTotalPages > 1"
                                                      v-model="fuelLogsCurrentPage"
                                                      :length="fuelLogsTotalPages"
                                                      class="py-0 pt-2"
                                                      density="compact"
                                                      @click="fetchFuelLogs"/>
                                    </v-col>
                                </v-row>
                            </v-card-text>

                            <v-card-text v-if="activeTab === 3">
                                <v-row class="mb-5">
                                    <v-col class="text-start">
                                        <v-btn color="black" size="small" @click="showCreateExpenseDialog">Add Expense
                                        </v-btn>
                                    </v-col>
                                </v-row>

                                <v-row>
                                    <v-col cols="12">
                                        <v-table density="compact" hover>
                                            <thead>
                                            <tr>
                                                <th>Label</th>
                                                <th>Receipt ID</th>
                                                <th>Payment Type</th>
                                                <th>Cost</th>
                                                <th>Tax</th>
                                                <th>Total Cost</th>
                                                <th>Timestamp</th>
                                                <th>Action</th>
                                            </tr>
                                            </thead>
                                            <tbody>
                                            <tr v-for="(expense, index) in expenses"
                                                v-if="!expenseProcessing && expenses.length" :key="index"
                                                class="text-left">
                                                <td>{{ expense.label }}</td>
                                                <td>{{ expense.receipt_id }}</td>
                                                <td>{{
                                                        getPaymentType(expense.driver_payment_type_id)?.name ?? ''
                                                    }}
                                                </td>
                                                <td>{{ expense.cost }}</td>
                                                <td>{{ expense.tax }}</td>
                                                <td>{{ expense.total_cost }}</td>
                                                <td>{{ formatDateTime(expense.created_at) }}</td>
                                                <td>
                                                    <v-btn color="black" size="x-small" variant="outlined"
                                                           @click="showExpenseDetailsDialog(expense)">Edit
                                                    </v-btn>
                                                    <ReceiptButton v-if="mayHaveReceipt(expense)" :endpoint="`fleet/expenses/${expense.id}/receipt`"/>
                                                </td>
                                            </tr>
                                            <tr v-else>
                                                <td v-if="expenseProcessing" colspan="7">
                                                    <v-progress-circular color="primary" indeterminate/>
                                                </td>
                                                <td v-else colspan="7">No Expenses found</td>
                                            </tr>
                                            </tbody>
                                        </v-table>

                                        <v-pagination v-if="expensesTotalPages > 1"
                                                      v-model="expensesCurrentPage"
                                                      :length="expensesTotalPages"
                                                      class="py-0 pt-2"
                                                      density="compact"
                                                      @click="fetchExpenses"/>
                                    </v-col>
                                </v-row>
                            </v-card-text>

                            <v-card-text v-if="tab.value === 7 && healthLoaded">
                                <VehicleHealth :asset-id="assetID" :asset-details="assetDetails"
                                               :dtc-status="dtcsStatus" :dtcs="dtcs"
                                               :dtc-tab-available="tabs.some(t => t.value === 4)"
                                               @no-access="onHealthNoAccess"
                                               @verdict="healthVerdict = $event"
                                               @show-dtcs="activeTab = 4; updateTab()"/>
                            </v-card-text>

                            <v-card-text v-if="activeTab === 4">
                                <v-row>
                                    <v-col cols="12">
                                        <v-table density="compact" hover>
                                            <thead>
                                            <tr>
                                                <th>Date Reported</th>
                                                <th>No. of Stored DTCs</th>
                                                <th>No. of Pending DTCs</th>
                                                <th>No. of Permanent DTCs</th>
                                            </tr>
                                            </thead>
                                            <tbody>
                                            <tr v-if="!dtcsProcessing && dtcs" class="text-left">
                                                <td>{{ formatCompanyDateTime(dtcs.event_datetime) }}</td>
                                                <td>{{ dtcs.no_of_stored_dtcs }}</td>
                                                <td>{{ dtcs.no_of_pending_dtcs }}</td>
                                                <td>{{ dtcs.no_of_permanent_dtcs }}</td>
                                            </tr>
                                            <tr v-else>
                                                <td v-if="dtcsProcessing" colspan="4">
                                                    <v-progress-circular color="primary" indeterminate/>
                                                </td>
                                                <td v-else colspan="4">No DTC Code found</td>
                                            </tr>
                                            </tbody>
                                        </v-table>
                                        <J1939LampChips :lamps="assetDetails.j1939_lamps"/>
                                        <v-table v-if="!dtcsProcessing && dtcs.description" density="compact">
                                            <thead>
                                            <tr>
                                                <th class="text-start">Type</th>
                                                <th class="text-start">Code</th>
                                                <th class="text-start">Technical Description</th>
                                                <th class="text-start">What It Means</th>
                                                <th class="text-start">Recommended Action</th>
                                            </tr>
                                            </thead>
                                            <tbody>
                                            <template v-for="(codes,type) in dtcs.description" :key="type">
                                                <tr v-for="(dtc, i) in codes" :key="dtc.code">
                                                    <td v-if="i === 0" :rowspan="codes.length" class="text-start align-top">
                                                        <v-chip size="small" variant="tonal" label
                                                                :color="dtcTypeColor(type)">
                                                            {{ type }}
                                                        </v-chip>
                                                    </td>
                                                    <td class="text-no-wrap align-top">{{ dtc.code }}</td>
                                                    <td class="align-top">{{ dtc.description }}</td>
                                                    <td class="align-top text-medium-emphasis">{{ dtc.user_description || '-' }}</td>
                                                    <td class="align-top text-success">
                                                        <template v-if="dtc.suggested_fix">
                                                            <v-icon class="me-1" icon="mdi-wrench" size="x-small"/>
                                                            {{ dtc.suggested_fix }}
                                                        </template>
                                                        <span v-else>-</span>
                                                    </td>
                                                </tr>
                                            </template>
                                            </tbody>
                                        </v-table>
                                    </v-col>
                                </v-row>
                            </v-card-text>

                            <v-card-text v-if="activeTab === 5">
                                <v-table density="compact">
                                    <thead>
                                    <tr>
                                        <th class="pa-0 px-1" style="width: 40px;"></th>
                                        <th class="pa-0 px-1 text-center" style="width: 50px;">Score</th>
                                        <th class="pa-0 px-1 text-left">{{ catConfig.personLabel || 'Driver' }}</th>
                                        <th class="pa-0 px-1 text-left">Time</th>
                                        <th class="pa-0 px-1 text-left">Duration</th>
                                        <th class="pa-0 px-1 text-left">Distance</th>
                                        <th class="pa-0 px-1 text-left">Max Speed</th>
                                        <th class="pa-0 px-1 text-left">Avg Speed</th>
                                        <th class="pa-0 px-1 text-left">Longest Idle</th>
                                        <th class="pa-0 px-1 text-left">Shortest Idle</th>
                                        <th class="pa-0 px-1 text-left">Avg Idle</th>
                                        <th class="pa-0 px-1 text-left">No. of Idles</th>
                                        <th class="pa-0 px-1 text-left">From</th>
                                        <th class="pa-0 px-1 text-left">To</th>
                                    </tr>
                                    </thead>

                                    <tbody>
                                    <template v-for="(trip, index) in trips" v-if="!tripsProcessing && trips.length" :key="trip.id">
                                        <tr class="text-left cursor-pointer" :data-trip-row-id="trip.id" @click="toggleTripExpand(index)">
                                            <td class="pa-0 px-1">
                                                <v-btn :icon="`mdi-${expandedTrip === index ? 'minus' : 'plus'}`"
                                                       density="compact" size="x-small" variant="text"/>
                                            </td>
                                            <td class="pa-0 px-1 text-center">
                                                <v-chip v-if="hasTripScore(trip)"
                                                        :color="tripScoreColor(trip.trip_score)"
                                                        size="x-small" variant="tonal"
                                                        class="font-weight-bold cursor-pointer"
                                                        @click.stop="selectedScoreTrip = trip; tripScoreDialogSwitch = true">
                                                    {{ formatTripScore(trip.trip_score) }}
                                                </v-chip>
                                                <span v-else class="text-grey text-caption">-</span>
                                            </td>
                                            <td>
                                                {{ trip.driver_name }}
                                                <div v-if="trip.driver_name && driverSourceLabel(trip.driver_source)"
                                                     class="text-caption text-medium-emphasis">
                                                    {{ driverSourceLabel(trip.driver_source) }}
                                                </div>
                                            </td>
                                            <td class="pa-0 px-1" style="min-width:125px;">{{
                                                    formatDateTime(trip.time)
                                                }}
                                            </td>
                                            <td class="pa-0 px-1">
                                                {{ new Date(trip.duration * 1000).toISOString().slice(11, 19) }}
                                            </td>
                                            <td class="pa-0 px-1">{{
                                                    strOperations().unitifyDistance(trip.distance, 'distance', 'km')
                                                }}
                                            </td>
                                            <td class="pa-0 px-1">
                                                {{
                                                    trip.max_speed > 0 ? strOperations().unitifyDistance(trip.max_speed) : 0
                                                }}
                                            </td>
                                            <td class="pa-0 px-1">{{
                                                    trip.median_speed > 0 ? strOperations().unitifyDistance(trip.median_speed) : 0
                                                }}
                                            </td>
                                            <td>
                                                {{
                                                    trip.idle_longest_time ? new Date(trip.idle_longest_time * 1000).toISOString().slice(11, 19) : ''
                                                }}
                                            </td>
                                            <td>
                                                {{
                                                    trip.idle_shortest_time ? new Date(trip.idle_shortest_time * 1000).toISOString().slice(11, 19) : ''
                                                }}
                                            </td>
                                            <td>
                                                {{
                                                    trip.idle_avg_time ? new Date(trip.idle_avg_time * 1000).toISOString().slice(11, 19) : ''
                                                }}
                                            </td>
                                            <td>{{ trip.total_idles }}</td>
                                            <td class="pa-0 px-1">
                                                <div class="d-flex align-center ga-1" @click.stop>
                                                    <v-tooltip v-if="trip.from" location="top">
                                                        <template v-slot:activator="{ props }">
                                                            <span class="text-caption" v-bind="props">{{ trip.from.length > 35 ? trip.from.substr(0, 35) + '...' : trip.from }}</span>
                                                        </template>
                                                        <span>{{ trip.from }}</span>
                                                    </v-tooltip>
                                                    <span v-else-if="trip.start_location?.lat" class="text-caption">{{ trip.start_location.lat }}, {{ trip.start_location.lon }}</span>
                                                    <v-tooltip v-if="trip.start_location?.lat" location="top" text="Open in Google Maps">
                                                        <template v-slot:activator="{ props: tp }">
                                                            <v-btn :href="`https://www.google.com/maps?q=${trip.start_location.lat},${trip.start_location.lon}`"
                                                                   target="_blank" icon="mdi-google-maps" size="x-small" variant="text"
                                                                   color="red" density="compact" v-bind="tp" @click.stop/>
                                                        </template>
                                                    </v-tooltip>
                                                    <v-tooltip v-if="trip.from || trip.start_location?.lat" location="top" text="Copy">
                                                        <template v-slot:activator="{ props: tp }">
                                                            <v-btn icon="mdi-content-copy" size="x-small" variant="text"
                                                                   color="grey" density="compact" v-bind="tp"
                                                                   @click.stop="copyLocation(trip,'from')"/>
                                                        </template>
                                                    </v-tooltip>
                                                </div>
                                            </td>
                                            <td class="pa-0 px-1">
                                                <div class="d-flex align-center ga-1" @click.stop>
                                                    <v-tooltip v-if="trip.to" location="top">
                                                        <template v-slot:activator="{ props }">
                                                            <span class="text-caption" v-bind="props">{{ trip.to.length > 35 ? trip.to.substr(0, 35) + '...' : trip.to }}</span>
                                                        </template>
                                                        <span>{{ trip.to }}</span>
                                                    </v-tooltip>
                                                    <span v-else-if="trip.end_location?.lat" class="text-caption">{{ trip.end_location.lat }}, {{ trip.end_location.lon }}</span>
                                                    <v-tooltip v-if="trip.end_location?.lat" location="top" text="Open in Google Maps">
                                                        <template v-slot:activator="{ props: tp }">
                                                            <v-btn :href="`https://www.google.com/maps?q=${trip.end_location.lat},${trip.end_location.lon}`"
                                                                   target="_blank" icon="mdi-google-maps" size="x-small" variant="text"
                                                                   color="red" density="compact" v-bind="tp" @click.stop/>
                                                        </template>
                                                    </v-tooltip>
                                                    <v-tooltip v-if="trip.to || trip.end_location?.lat" location="top" text="Copy">
                                                        <template v-slot:activator="{ props: tp }">
                                                            <v-btn icon="mdi-content-copy" size="x-small" variant="text"
                                                                   color="grey" density="compact" v-bind="tp"
                                                                   @click.stop="copyLocation(trip,'to')"/>
                                                        </template>
                                                    </v-tooltip>
                                                </div>
                                            </td>
                                        </tr>
                                        <!-- Expanded trip detail row -->
                                        <tr v-if="expandedTrip === index">
                                            <td :colspan="14" class="pa-0">
                                                <div class="trip-expanded-content pa-1">
                                                    <v-row class="align-stretch">
                                                        <v-col cols="12" md="4">
                                                            <v-table density="compact" class="trip-stats-table">
                                                                <tbody>
                                                                <tr v-if="trip.driver_name">
                                                                    <td class="font-weight-bold">Driver</td>
                                                                    <td>
                                                                        {{ trip.driver_name }}
                                                                        <span v-if="driverSourceLabel(trip.driver_source)"
                                                                              class="text-caption text-medium-emphasis">
                                                                            ({{ driverSourceLabel(trip.driver_source) }})
                                                                        </span>
                                                                    </td>
                                                                </tr>
                                                                <tr v-if="tagDisagrees(trip)">
                                                                    <td class="font-weight-bold">Driver tag</td>
                                                                    <td class="text-warning">The tag seen belongs to another driver</td>
                                                                </tr>
                                                                <tr>
                                                                    <td class="font-weight-bold">Max Speed</td>
                                                                    <td>{{ trip.max_speed > 0 ? strOperations().unitifyDistance(trip.max_speed) : 0 }}</td>
                                                                </tr>
                                                                <tr>
                                                                    <td class="font-weight-bold">Avg Speed</td>
                                                                    <td>{{ trip.median_speed > 0 ? strOperations().unitifyDistance(trip.median_speed) : 0 }}</td>
                                                                </tr>
                                                                <tr>
                                                                    <td class="font-weight-bold">Duration</td>
                                                                    <td>{{ new Date(trip.duration * 1000).toISOString().slice(11, 19) }}</td>
                                                                </tr>
                                                                <tr>
                                                                    <td class="font-weight-bold">Distance</td>
                                                                    <td>{{ strOperations().unitifyDistance(trip.distance, 'distance', 'km') }}</td>
                                                                </tr>
                                                                <tr>
                                                                    <td class="font-weight-bold">No. of Idles</td>
                                                                    <td>{{ trip.total_idles }}</td>
                                                                </tr>
                                                                <tr v-if="trip.idle_avg_time">
                                                                    <td class="font-weight-bold">Avg Idle</td>
                                                                    <td>{{ new Date(trip.idle_avg_time * 1000).toISOString().slice(11, 19) }}</td>
                                                                </tr>
                                                                <!-- Driver Analytics (from firmware) -->
                                                                <tr v-if="trip.driver_analytics" class="bg-blue-lighten-5">
                                                                    <td class="font-weight-bold" colspan="2">
                                                                        <v-icon size="x-small" icon="mdi-car-brake-alert" class="me-1"/>Driver Analytics
                                                                    </td>
                                                                </tr>
                                                                <tr v-if="trip.driver_analytics">
                                                                    <td class="font-weight-bold">Harsh Braking</td>
                                                                    <td>{{ trip.driver_analytics.harsh_braking_count }} <span v-if="trip.driver_analytics.max_brake_mg > 0" class="text-grey text-caption">(peak {{ (trip.driver_analytics.max_brake_mg / 1000).toFixed(2) }}g)</span></td>
                                                                </tr>
                                                                <tr v-if="trip.driver_analytics">
                                                                    <td class="font-weight-bold">Harsh Acceleration</td>
                                                                    <td>{{ trip.driver_analytics.harsh_accel_count }} <span v-if="trip.driver_analytics.max_accel_mg > 0" class="text-grey text-caption">(peak {{ (trip.driver_analytics.max_accel_mg / 1000).toFixed(2) }}g)</span></td>
                                                                </tr>
                                                                <tr v-if="trip.driver_analytics">
                                                                    <td class="font-weight-bold">Sharp Cornering</td>
                                                                    <td>{{ trip.driver_analytics.sharp_cornering_count }} <span v-if="trip.driver_analytics.max_corner_mg > 0" class="text-grey text-caption">(peak {{ (trip.driver_analytics.max_corner_mg / 1000).toFixed(2) }}g)</span></td>
                                                                </tr>
                                                                <tr v-if="trip.driver_analytics && trip.driver_analytics.crash_count > 0">
                                                                    <td class="font-weight-bold text-red">Crash Detected</td>
                                                                    <td class="text-red">{{ trip.driver_analytics.crash_count }} <span class="text-caption">({{ (trip.driver_analytics.max_impact_mg / 1000).toFixed(2) }}g impact)</span></td>
                                                                </tr>
                                                                <tr v-if="trip.driver_analytics && trip.driver_analytics.speeding_seconds > 0">
                                                                    <td class="font-weight-bold">Speeding</td>
                                                                    <td>{{ formatDuration(trip.driver_analytics.speeding_seconds) }}</td>
                                                                </tr>
                                                                <tr v-if="trip.driver_analytics && trip.driver_analytics.max_speed_kmh_x10 > 0">
                                                                    <td class="font-weight-bold">Max Speed (OBD)</td>
                                                                    <td>{{ strOperations().unitifyDistance(trip.driver_analytics.max_speed_kmh_x10 / 10, 'speed', 'km') }}</td>
                                                                </tr>
                                                                <tr>
                                                                    <td class="font-weight-bold">From</td>
                                                                    <td>
                                                                        <span v-if="trip.from">{{ trip.from }}</span>
                                                                        <span v-else-if="trip.start_location">{{ trip.start_location.lat }}, {{ trip.start_location.lon }}</span>
                                                                        <a v-if="trip.start_location"
                                                                           :href="`https://www.google.com/maps?q=${trip.start_location.lat},${trip.start_location.lon}`"
                                                                           target="_blank" class="ms-1">
                                                                            <v-icon size="x-small" icon="mdi-open-in-new"/>
                                                                        </a>
                                                                    </td>
                                                                </tr>
                                                                <tr>
                                                                    <td class="font-weight-bold">To</td>
                                                                    <td>
                                                                        <span v-if="trip.to">{{ trip.to }}</span>
                                                                        <span v-else-if="trip.end_location">{{ trip.end_location.lat }}, {{ trip.end_location.lon }}</span>
                                                                        <a v-if="trip.end_location"
                                                                           :href="`https://www.google.com/maps?q=${trip.end_location.lat},${trip.end_location.lon}`"
                                                                           target="_blank" class="ms-1">
                                                                            <v-icon size="x-small" icon="mdi-open-in-new"/>
                                                                        </a>
                                                                    </td>
                                                                </tr>
                                                                </tbody>
                                                            </v-table>
                                                        </v-col>
                                                        <v-col cols="12" md="8" class="d-flex">
                                                            <div class="flex-grow-1" style="min-height: 200px;">
                                                                <trip-route-map
                                                                    :selected-device="tripDevice"
                                                                    :selected-trip="trip"
                                                                />
                                                            </div>
                                                        </v-col>
                                                    </v-row>
                                                </div>
                                            </td>
                                        </tr>
                                    </template>
                                    <tr v-else>
                                        <td v-if="tripsProcessing" colspan="14">
                                            <v-progress-circular color="primary" indeterminate/>
                                        </td>
                                        <td v-else colspan="14">No recent trips</td>
                                    </tr>
                                    </tbody>
                                </v-table>

                                <v-pagination v-if="tripsTotalPages > 1" v-model="tripsCurrentPage"
                                              :length="tripsTotalPages" class="py-0 pt-2"
                                              density="compact" @click="fetchTrips()"/>
                            </v-card-text>
                        </v-card>
                    </v-tabs-window-item>
                </v-tabs-window>
            </v-col>
        </v-row>
        <ResolveDefectDialog v-model="dvirResolveOpen" :defects="dvirResolving" @resolved="onDvirResolved"/>
    </v-container>

    <create-task v-if="createTaskDialog" v-model="createTaskDialog"
                 :asset="asset" :drivers="driversList"
                 :inspection-task="selectedTask"
                 :service-type="selectedServiceType"
                 :inspection-tasks-list="inspectionTasksList"
                 :service-centers="serviceCenters"
                 :services="selectedServices"
                 @add-task="addTask"/>

    <task-details v-if="taskDetailsDialog" v-model="taskDetailsDialog"
                  :driver-payment-types="driverPaymentTypes"
                  :asset="asset"
                  :task="selectedTask"
                  :drivers="driversList"
                  :service-centers="serviceCenters"
                  :services="selectedServices"
                  @create-linked-maintenance-task="createLinkedMaintenanceTask"
                  @add-service-center="addServiceCenter"
                  @items-changed="reloadTaskList(selectedServiceType)"
                  @update-task="updateTask"/>

    <task-logs v-if="taskLogsDialog" v-model="taskLogsDialog"
               :task="selectedTask" @update:model-value="()=>{selectedTask = null}"/>

    <fuel-log-create-edit v-if="createFuelLogDialog" v-model="createFuelLogDialog"
                          :asset="asset"
                          :fuel-types="fuelTypes"
                          :payment-types="driverPaymentTypes"
                          :record="selectedFuelLog"
                          @add-fuel-log="addFuelLog"
                          @update-fuel-log="updateFuelLog"/>

    <create-expense v-if="createExpenseDialog" v-model="createExpenseDialog"
                    :asset="asset"
                    :driver-payment-types="driverPaymentTypes"
                    :drivers="driversList"
                    :expense-types="expenseTypes"
                    @add-expense="addExpense"/>

    <expense-details v-if="expenseDetailsDialog" v-model="expenseDetailsDialog"
                     :asset="asset"
                     :driver-payment-types="driverPaymentTypes"
                     :drivers="driversList"
                     :expense="selectedExpense"
                     :expense-types="expenseTypes"
                     @update-expense="updateExpense"/>

    <edit-asset v-if="editDialogSwitch" v-model="editDialogSwitch" :asset="asset" @updateAsset="onAssetUpdated"/>
    <EditUpdateAssetGroup v-if="assetGroupDialogSwitch" v-model="assetGroupDialogSwitch" :asset="asset"
                          @updateAsset="onAssetGroupUpdated"/>
    <AssetMonitoringConfig v-if="monitoringConfigDialogSwitch" v-model="monitoringConfigDialogSwitch"
                           :asset="asset" @updateAsset="onMonitoringConfigUpdated"/>
    <TripScoreDialog
        v-if="tripScoreDialogSwitch"
        v-model="tripScoreDialogSwitch"
        :trip="selectedScoreTrip"
        @view-on-map="onTripScoreViewOnMap"
    />
</template>

<script setup>
import {computed, onMounted, onBeforeUnmount, ref} from "vue";
import {refreshMenuCounts} from "@/composables/useMenuCounts";
import {useRoute, useRouter} from "vue-router";
import {geocodeLatLng, isValidAddress} from "@/composables/useGeocode";
import {send} from "@/composables/apiClient";
import {driverSourceLabel, tagDisagrees} from "@/composables/tripDriverSource";
import {useAuthStore} from "@/stores/authStore";
import UserAvatar from "@/components/UserAvatar.vue";
import ThBtn from "@/components/globals/th-btn.vue";
import {strOperations, useNavigation} from "@/composables/globals";
import {formatCompanyDateTime, formatDateTime} from "@/composables/useTimezone";
import {
    assessDiagnostics, DATE_ONLY, diagnosticBubble, diagnosticsClear, readLamps,
} from "@/composables/vehicleHealthRule";
import {useTripScore} from "@/composables/useTripScore";
import TripScoreDialog from "@/components/dialogs/trips/TripScoreDialog.vue";
import ResolveDefectDialog from "@/components/inspections/ResolveDefectDialog.vue";
import {useClipboard} from "@/composables/clipboard";
import {useSnackbarStore} from "@/stores/snackBarStore";
import {getCategoryConfig} from "@/composables/categoryConfig";
import {getStoppedLabel} from "@/composables/fleetStatus";
import {powerSourceIcon, primaryBatteryType} from "@/composables/usePowerSourceIcon";
import TaskLogs from "@/pages/Asset/Details/TaskLogs.vue";
import CreateTask from "@/pages/Asset/Details/CreateTask.vue";
import TaskDetails from "@/pages/Asset/Details/TaskDetails.vue";
import CreateExpense from "@/pages/Asset/Details/CreateExpense.vue";
import ExpenseDetails from "@/pages/Asset/Details/ExpenseDetails.vue";
import FuelLogCreateEdit from "@/pages/Asset/Details/FuelLog_Create_Edit.vue";
import ReceiptButton from "@/pages/Asset/Details/ReceiptButton.vue";
import EditAsset from "@/components/dialogs/assets/Edit.vue";
import EditUpdateAssetGroup from "@/components/dialogs/assets/EditUpdateAsset.vue";
import AssetMonitoringConfig from "@/components/dialogs/assets/AssetMonitoringConfig.vue";
import TripRouteMap from "@/components/cards/TripRouteMap.vue";
import J1939LampChips from "@/components/cards/assets/J1939LampChips.vue";
import VehicleHealth from "@/pages/Asset/Details/VehicleHealth.vue";
import {LMap, LTileLayer, LMarker, LIcon} from "@vue-leaflet/vue-leaflet";
import "leaflet/dist/leaflet.css";
import NoteIcon from "@/components/dialogs/notes/NoteIcon.vue";
import MenuCountChip from "@/components/layouts/MenuCountChip.vue";

const locationMap = ref(null);
const onLocationMapReady = () => {
    if (locationMap.value) {
        locationMap.value.leafletObject.attributionControl?.remove();
    }
};

const router = useRouter();
const {navigateTo} = useNavigation();
const editDialogSwitch = ref(false);
const showDeviceInfo = ref(false);
const {user} = useAuthStore();
const asset = ref(null);
const {copyToClipboard} = useClipboard();
const {showSnackbar} = useSnackbarStore();
const assetID = router.currentRoute.value.params.asset;
const deviceStatuses = [
    'Active',
    'Disabled',
    'Faulty',
    'Lost'
];

const statusColor = (status) => {
    switch (status) {
        case 1:
            return '#ff9e01'
        case 2:
            return '#849e00'
        case 3:
            return '#099f00'
    }
}
const getObjVal = (obj, path) => {
    return path.split('.').reduce((acc, key) => acc?.[key], obj);
}
const openGoogleMaps = () => {
    const lat = assetDetails.value?.device_status?.latitude;
    const lng = assetDetails.value?.device_status?.longitude;
    if (lat && lng) {
        window.open(`https://www.google.com/maps?q=${lat},${lng}`, '_blank');
    }
};

const assetRoute = useRoute();
const initialTab = assetRoute.query.tab ? parseInt(assetRoute.query.tab) : 6;
const activeTab = ref(initialTab);

const catConfig = computed(() => getCategoryConfig(asset.value?.asset_type?.category));

const NON_REPORTING_HOURS = 30;

const fleetStatusMap = {
    moving: { label: 'Moving', color: 'green', icon: 'mdi-car-connected' },
    idling: { label: 'Idling', color: 'amber', icon: 'mdi-car-clock' },
    offline: { label: 'Offline', color: 'red', icon: 'mdi-signal-off' },
};

const fleetStatusBadge = computed(() => {
    const a = assetDetails.value;
    if (!a?.fleet_status) return { label: 'No Device', color: undefined, icon: 'mdi-link-off', variant: 'outlined' };
    if (a.fleet_status === 'parked') {
        const cat = (a.asset_type_category || a.asset_type?.category || '').toLowerCase();
        const label = (cat === 'vehicle' || cat === 'equipment') ? 'Parked' : 'Stationary';
        return { label, color: 'grey', icon: 'mdi-car-brake-parking' };
    }
    return fleetStatusMap[a.fleet_status] || fleetStatusMap.offline;
});

const isAssetOffline = computed(() => {
    const a = assetDetails.value;
    return a?.fleet_status === 'offline' || !a?.last_updated;
});

// J1939 (heavy duty) trackers report the DM1 red stop, amber warning and protect
// lamps besides the MIL; any of them lit (value 1) is an engine fault too.
const j1939LampLit = (a) => ['red_stop_lamp', 'amber_warning_lamp', 'protect_lamp']
    .some(lamp => a?.j1939_lamps?.[lamp] === 1);

const isAssetHealthy = computed(() => {
    const a = assetDetails.value;
    if (!a?.slno) return false;
    if (isAssetOffline.value) return false;
    if ((a.signal_bars ?? 0) < 2) return false;
    if ((a.battery_bars ?? 0) < 1) return false;
    if (!a.obd_present) return true;
    if (['warning', 'error'].includes(healthVerdict.value)) return false;
    const {lamps, diagnostics} = engineReport.value;
    // With neither engine tab the codes are never loaded, so only the lamps can speak
    return tabs.value.some(t => t.value === 4 || t.value === 7)
        ? diagnosticsClear({diagnostics, codeStatus: dtcsStatus.value})
        : !lamps.lit && !lamps.errors;
});

const signalIcon = (strength) => {
    switch (strength) {
        case 0: return { icon: 'mdi-signal-off', color: '#dbdbdb' };
        case 1: return { icon: 'mdi-signal-cellular-outline', color: '#d70000' };
        case 2: return { icon: 'mdi-signal-cellular-1', color: '#ff9e01' };
        case 3: return { icon: 'mdi-signal-cellular-2', color: '#849e00' };
        case 4: case 5: return { icon: 'mdi-signal-cellular-3', color: '#099f00' };
        default: return { icon: 'mdi-signal-off', color: '#dbdbdb' };
    }
};

const batteryIcon = (strength, type = null) => powerSourceIcon(strength, type);

// The SKU-selected battery channel, in millivolts. AssetDetailsResource sends
// raw voltage1/voltage2 and keeps the flags under device.product_sku; picking
// "whichever channel is non-zero" showed a PRO-V2's external rail instead of
// its channel-1 battery and, on a stale OBD unit, the ~2.8 V supercap. Same
// precedence as the resource's battery_bars: channel 1 if battery1_status,
// else channel 2 if battery2_status; the old pick stays as the flag-less fallback.
const primaryVoltageMv = computed(() => {
    const a = assetDetails.value;
    if (!a) return null;
    const sku = a.device?.product_sku;
    if (sku?.battery1_status) return a.voltage1;
    if (sku?.battery2_status) return a.voltage2;
    return a.voltage2 > 0 ? a.voltage2 : a.voltage1;
});

const otaStatusLabel = (status) => {
    const labels = { 1: 'Queued', 2: 'Queued', 3: 'Acknowledged', 4: 'Downloading', 5: 'Downloaded', 6: 'Failed', 7: 'Upgraded' };
    return labels[status] || `Status ${status}`;
};
const otaStatusColor = (status) => {
    if (status === 7) return 'success';
    if (status === 6) return 'error';
    if (status >= 3 && status <= 5) return 'info';
    return 'grey';
};

const allTabs = [
    {value: 6, title: 'Driver Inspections (DVIR)'},
    {value: 0, title: 'Scheduled Inspections'},
    {value: 1, title: 'Maintenance Services'},
    {value: 2, title: 'Fuel Refills', requiresCat: 'showFuelTab'},
    {value: 3, title: 'Expenses'},
    {value: 7, title: 'Vehicle Health', requiresCat: 'showHealthTab'},
    {value: 4, title: 'Diagnostic Codes', requiresCat: 'showDtcTab'},
    {value: 5, title: 'Trips'},
];
const tabs = computed(() => allTabs.filter(tab => {
    if (tab.requiresCat && !catConfig.value[tab.requiresCat]) return false;
    if ((tab.value === 4 || tab.value === 7) && !assetDetails.value?.obd_present) return false;
    return true;
}));
// Set when the vehicle-health API refuses this role ("Action not permitted!"), the same answer
// that makes Vehicle Health show its no-access note
const healthDenied = ref(false);
// The Vehicle Health banner colour, once that tab has been opened
const healthVerdict = ref(null);
// The engine check icon and the auto-open land on Vehicle Health when the vehicle has it and the role may see it
const engineCheckTab = computed(() => (!healthDenied.value && tabs.value.some(t => t.value === 7) ? 7 : 4));
// Vehicle Health was opened for an engine check, so a refusal falls back to the DTC tab
const healthFromEngineCheck = ref(false);
const openEngineCheck = () => {
    activeTab.value = engineCheckTab.value;
    healthFromEngineCheck.value = activeTab.value === 7;
    updateTab();
};
const onHealthNoAccess = () => {
    healthDenied.value = true;
    if (healthFromEngineCheck.value && activeTab.value === 7 && tabs.value.some(t => t.value === 4)) {
        activeTab.value = 4;
        updateTab();
    }
    healthFromEngineCheck.value = false;
};

const asset_table = computed(() => {
    const cc = catConfig.value;
    const rows = [];

    if (cc.showVin) {
        rows.push([
            {key: 'vin', title: 'Assigned VIN'},
            {key: 'acquired_vin', title: 'Acquired VIN'},
        ]);
        rows.push([
            {key: 'license_plate', title: 'License Plate'},
            {key: 'owner_email', title: 'Owner Email'},
        ]);
    }
    if (cc.showMakeModelYear) {
        rows.push([
            {key: 'vehicle_make', fallbackKey: 'vehicle_details.wmi.manufacturer', title: 'Make'},
            {key: 'vehicle_model', fallbackKey: 'vehicle_details.wmi.vehicle_type', title: 'Model'},
        ]);
        rows.push([
            {key: 'vehicle_year', fallbackKey: 'vehicle_details.vis.model_year', title: 'Year'},
            ...(cc.showOdometer ? [{key: 'device_status.odometer', title: 'Current Odometer', type: 'distance', baseUnit: 'km'}] : []),
        ]);
    } else if (cc.showOdometer) {
        rows.push([
            {key: 'device_status.odometer', title: 'Current Odometer', type: 'distance', baseUnit: 'km'},
        ]);
    }

    rows.push([
        {key: 'status', title: 'Asset Status'},
        {key: 'asset_type.name', title: 'Asset Type'},
    ]);
    rows.push([
        {key: 'last_inspection_date', title: 'Last Inspection Date', type: 'date'},
        {key: 'last_service_date', title: 'Last Service Date', type: 'date'},
    ]);
    rows.push([
        {key: 'pending_inspection', title: 'Pending Inspections'},
        {key: 'pending_maintenance', title: 'Pending Services'},
    ]);
    rows.push([
        {key: 'total_trips', title: 'Total Trips'},
        {key: 'total_miles', title: 'Total Distance', type: 'distance', baseUnit: 'km'},
    ]);

    const personRow = [];
    if (cc.personLabel) {
        personRow.push({key: 'driver_name', title: cc.personLabel});
    }
    personRow.push({key: 'device_status.updated_at', title: 'Last Communicated', type: 'datetime'});
    rows.push(personRow);

    return rows;
});
const assetDetails = computed(() => {
    return {
        ...asset.value,
        status: deviceStatuses[asset.value?.device?.status],
        device_status: {...asset.value?.device?.device_status},
        asset_type: {...asset.value?.asset_type},
        pending_inspection: pendingInspectionTaskCounts.value,
        pending_maintenance: pendingMaintenanceTaskCounts.value,
    };
});
const driverPaymentTypes = ref([]);
const driversList = ref([]);

onMounted(async () => {
    await send('get', `assets/${assetID}/details`)
        .then((response) => {
            asset.value = response.data.data.asset;
            getServiceTaskCounts();
            fetchInspections();
            fetchInspectionTasks();
        })
        .catch((error) => {
            showSnackbar(error.response.data.message ?? 'Asset could not be loaded', 'error')
        });

    if (asset.value.driver_id) {
        fetchDriverPaymentTypes();
    }
    if (asset.value.company_id) {
        fetchDriversList();
    }
    // Restore requested tab now that asset data (and thus tabs list) is available
    const requestedTab = assetRoute.query.tab ? parseInt(assetRoute.query.tab) : null;
    if (requestedTab !== null && tabs.value.some(t => t.value === requestedTab)) {
        activeTab.value = requestedTab;
    } else if (!tabs.value.some(t => t.value === activeTab.value)) {
        // Reset tab if current tab is hidden for this category
        activeTab.value = 6;
    }
    // Auto-open Vehicle Health (else the DTC tab) if no tab was requested and DTCs are present
    if (!requestedTab && activeTab.value === 6
        && (asset.value.obd_pending_dtcs || asset.value.obd_permanent_dtcs || j1939LampLit(asset.value))
        && tabs.value.some(t => t.value === engineCheckTab.value)) {
        activeTab.value = engineCheckTab.value;
        healthFromEngineCheck.value = activeTab.value === 7;
    }
    if (activeTab.value > 0) {
        updateTab();
    }
    // The out-of-service banner needs the open defects regardless of which tab is
    // active; the DVIR tab (6) already loads them via updateTab.
    if (activeTab.value !== 6) {
        fetchDvirDefects(true);
    }
    // The diagnostic codes tab count lists each code once, so load the codes quietly up front
    if (asset.value?.obd_present && tabs.value.some(t => t.value === 4 || t.value === 7) && ![4, 7].includes(activeTab.value)) {
        fetchDtcs(true);
    }
    if (assetRoute.query.edit === '1') {
        editDialogSwitch.value = true;
    }
    openTaskFromLink();
});

const onAssetUpdated = (updatedAsset) => {
    asset.value = updatedAsset;
    editDialogSwitch.value = false;

    // Re-fetch full details to pick up observer-parsed vehicle info (make/model/year)
    send('get', `assets/${assetID}/details`)
        .then((response) => {
            asset.value = response.data.data.asset;
            // An edit can make this an OBD vehicle; the tick and bubble wait for the codes
            if (asset.value?.obd_present && !dtcsStatus.value && tabs.value.some(t => t.value === 4 || t.value === 7)) {
                fetchDtcs(true);
            }
        })
        .catch(() => {});
};

const assetGroupDialogSwitch = ref(false);
const openAssetGroupDialog = () => {
    assetGroupDialogSwitch.value = true;
};
const onAssetGroupUpdated = (updatedAsset) => {
    asset.value = updatedAsset;
    assetGroupDialogSwitch.value = false;
};
const assetGroupDetach = (assetGroup) => {
    if (confirm(`Are you sure to detach "${assetGroup.name}" from "${asset.value.label}" asset?`)) {
        send('delete', `assets/${assetID}/asset-group/${assetGroup.id}`)
            .then((response) => {
                showSnackbar(response.data.message);
                if (response.data.success) {
                    asset.value.asset_groups = asset.value.asset_groups.filter(g => g.id !== assetGroup.id);
                }
            })
            .catch((errorResponse) => {
                showSnackbar(errorResponse.response?.data?.message ?? 'Something went wrong', 'error');
            });
    }
};

const monitoringConfigDialogSwitch = ref(false);
const openMonitoringConfigDialog = () => {
    monitoringConfigDialogSwitch.value = true;
};
const onMonitoringConfigUpdated = (updatedAsset) => {
    asset.value = updatedAsset;
    monitoringConfigDialogSwitch.value = false;
};
const monitoringConfigDetach = (monitoringConfig) => {
    if (confirm(`Are you sure to detach "${monitoringConfig.name}" from "${asset.value.label}" asset?`)) {
        const remainingIds = (asset.value.monitoring_configs || [])
            .filter(c => c.id !== monitoringConfig.id)
            .map(c => c.id);
        send('post', `assets/${assetID}/monitoring-configs`, {monitoring_configs: remainingIds})
            .then((response) => {
                showSnackbar(response.data.message);
                if (response.data.success) {
                    asset.value = response.data.data;
                }
            })
            .catch((errorResponse) => {
                showSnackbar(errorResponse.response?.data?.message ?? 'Something went wrong', 'error');
            });
    }
};

/***************
 *** Methods ***
 ***************/
const healthLoaded = ref(false);
const updateTab = () => {
    switch (activeTab.value) {
        case 6:
            if (!dvirLoaded.value) {
                dvirLoaded.value = true;
                fetchDvir();
            }
            break;
        case 0:
            break;
        case 1:
            if (!maintenances.value.length) {
                fetchMaintenances();
                fetchServiceCenters();
                fetchInspectionTasksList();
                fetchMaintenanceTasks();
            }
            break;
        case 2:
            if (!fuelTypes.value.length)
                fetchFuelTypes()
            if (!fuelLogs.value.length)
                fetchFuelLogs()
            break;
        case 3:
            if (!expenseTypes.value.length)
                fetchExpenseTypes()
            if (!expenses.value.length)
                fetchExpenses()
            break;
        case 4:
            if (!dtcsProcessing.value && !dtcsAnswered.value)
                fetchDtcs()
            break;
        case 5:
            if (!trips.value.length)
                fetchTrips()
            break;
        case 7:
            healthLoaded.value = true;
            if (!dtcsProcessing.value && !dtcsAnswered.value)
                fetchDtcs(true)
            break;
    }
}

const createTaskDialog = ref(false)
const selectedServiceType = ref(1)
const selectedServices = computed(() => {
    if (selectedServiceType.value !== null) {
        if (selectedServiceType.value === 0) {
            return maintenances.value;
        } else {
            return inspections.value;
        }
    }
    return [];
})
const showCreateTaskDialog = ((type = 1, reset = true) => {
    if (reset) {
        selectedTask.value = null;
    }
    selectedServiceType.value = type;
    createTaskDialog.value = true;
})


const selectedTask = ref(null)
const taskDetailsDialog = ref(false)
const pendingInspectionTaskCounts = ref(0)
const pendingMaintenanceTaskCounts = ref(0)
const showTaskDetailsDialog = ((task) => {
    selectedTask.value = task;
    selectedServiceType.value = task.type;
    taskDetailsDialog.value = true;
})

// ?task=<id> from a report opens that order's details once, then leaves the URL.
// Fetched rather than taken from the list, which may be on another page.
const openTaskFromLink = () => {
    const taskId = assetRoute.query.task;
    if (!taskId) return;
    const clearLink = () => {
        const {task, ...query} = assetRoute.query;
        router.replace({query});
    };
    if (!/^\d+$/.test(String(taskId))) {
        clearLink();
        return;
    }
    send('get', `fleet/service-tasks/${taskId}`)
        .then((response) => {
            const task = response.data.data;
            if (String(task?.asset_id) !== String(assetID)) {
                showSnackbar('That order is not on this vehicle', 'error');
                return;
            }
            const taskTab = task.type === 0 ? 1 : 0;
            if (activeTab.value !== taskTab) {
                activeTab.value = taskTab;
                updateTab();
            }
            showTaskDetailsDialog(task);
        })
        .catch(() => showSnackbar('That order could not be loaded', 'error'))
        .finally(clearLink);
};

// The create and update answers carry no due date or linked records, so the row comes from the list
const reloadTaskList = (type) => (type === 0 ? fetchMaintenanceTasks() : fetchInspectionTasks());

const createLinkedMaintenanceTask = (task) => {
    selectedTask.value = task;
    showCreateTaskDialog(0, false);
    activeTab.value = 1;
    updateTab();
}
const addTask = (task) => {
    if (selectedServiceType.value === 0) {
        if (task.inspection_task_id) {
            const taskIndex = inspectionTasks.value.findIndex((item) => item.id === task.inspection_task_id)
            if (taskIndex > -1) {
                const updatedTask = {
                    ...inspectionTasks.value[taskIndex],
                    maintenance_task: {id: task.id, label: task.label},
                };
                inspectionTasks.value.splice(taskIndex, 1, updatedTask);

                task.inspection_task = {
                    id: updatedTask.id,
                    label: updatedTask.label,
                };
            }
        }
        maintenanceTasks.value.unshift(task);
    } else {
        inspectionTasks.value.unshift(task);
        inspectionTasksList.value.unshift({
            id: task.id,
            label: task.label,
        });
    }
    getServiceTaskCounts();
    reloadTaskList(selectedServiceType.value);
}
const updateTask = (task) => {
    getServiceTaskCounts();
    // Also books the next repeating inspection on the server, and completing a work order resolves its defect
    reloadTaskList(task.type);
    if (task.type === 0) {
        fetchDvirDefects(true);
    }
    selectedTask.value = null;
}

const taskLogsDialog = ref(false)
const showTaskLogsDialog = ((task) => {
    selectedTask.value = task;
    taskLogsDialog.value = true;
})


const fetchDriversList = () => {
    send('get', `companies/drivers?list=true`, {company: asset.value.company_id})
        .then((response) => {
            driversList.value = response.data.data;
        })
        .catch((error) => {
            showSnackbar('Drivers List could not be loaded', 'error')
        })
}
const fetchDriverPaymentTypes = () => {
    // The company's shared cards plus the assigned driver's own. company is
    // only read for admins; everyone else is pinned to their own company.
    send('get', `driver-payment-types`, {
        list: true,
        driver: asset.value.driver_id,
        company: asset.value.company_id,
    }).then((response) => {
        driverPaymentTypes.value = response.data.data;
    }).catch((error) => {
        showSnackbar(error.response.data.message ?? "Driver's payment types could not be loaded", 'error')
    });
}
const getPaymentType = (id) => {
    return driverPaymentTypes.value.find((record) => record.id === id)
}
const getFuelType = (id) => {
    return fuelTypes.value.find((record) => record.id === id)
}
const getServiceTaskCounts = () => {
    send('get', `fleet/service-tasks/count`, {
        asset: assetID
    }).then((response) => {
        pendingMaintenanceTaskCounts.value = response.data.data?.maintenance ?? 0;
        pendingInspectionTaskCounts.value = response.data.data?.inspection ?? 0;
    }).catch((error) => {
        console.log(error);
    });
}

/*******************************
 *** Inspections Tab Start ***
 *******************************/

const inspections = ref([]);
const inspectionTasks = ref([]);
const inspectionsProcessing = ref(false);
const inspectionTasksTotalPages = ref(1);
const inspectionTasksCurrentPage = ref(0);

const fetchInspections = () => {
    // Not gated on the asset type: the list is not filtered by it, and a
    // vehicle with no type set could not be given a scheduled inspection.
    {
        inspectionsProcessing.value = true;
        send('get', `fleet/service-types`, {
            type: 1,
            list: 1,
        })
            .then((response) => {
                inspections.value = response.data.data ?? [];
            })
            .catch((error) => {
                showSnackbar(error.response.data.message ?? 'Inspections could not be loaded', 'error')
            }).finally(() => inspectionsProcessing.value = false);
    }
}
// Saves can reload a list twice in a row, so only the newest answer counts
let inspectionTasksRequest = 0;
const fetchInspectionTasks = () => {
    const request = ++inspectionTasksRequest;
    inspectionsProcessing.value = true;
    send('get', `fleet/service-tasks`, {
        type: 1,
        asset: assetID,
        page: inspectionTasksCurrentPage.value,
    })
        .then((response) => {
            if (request !== inspectionTasksRequest) return;
            inspectionTasks.value = response.data.data.data ?? [];
            inspectionTasksTotalPages.value = response.data.data.meta.last_page;
            inspectionTasksCurrentPage.value = response.data.data.meta.current_page;
        })
        .catch((error) => {
            if (request !== inspectionTasksRequest) return;
            showSnackbar(error.response?.data?.message ?? 'Inspection tasks could not be loaded', 'error')
        }).finally(() => {
            if (request === inspectionTasksRequest) inspectionsProcessing.value = false;
        });
}

/*****************************
 *** Inspections Tab End ***
 *****************************/

/***********************************
 *** Inspections (DVIR) Tab Start ***
 ***********************************/

const dvirInspections = ref([]);
const dvirInspectionsPage = ref(1);
const dvirInspectionsTotalPages = ref(1);
const dvirDefects = ref([]);
// Open defects on the vehicle, from the list's total so the tab count is not capped at one page
const dvirDefectsTotal = ref(0);
const dvirProcessing = ref(false);
const dvirBusy = ref(null);
const dvirLoaded = ref(false);

const dvirOpenDefectsCount = computed(() => dvirDefects.value.filter(d => d.status !== 'resolved').length);

// The same rule as the Vehicle Health verdict, for the tab bubble and the header tick
const engineReport = computed(() => {
    const a = assetDetails.value ?? {};
    const lamps = readLamps({
        j1939: a.j1939_lamps, checkEngine: a.alert_lamb_status, at: a.lamps_at, timed: 'lamps_at' in a,
    });
    const reportedCount = Math.max(...[a.obd_stored_dtcs, a.obd_pending_dtcs, a.obd_permanent_dtcs].map(n => Number(n) || 0));
    const diagnostics = assessDiagnostics({
        lamps,
        codeStatus: dtcsStatus.value,
        groups: dtcs.value?.description,
        codesAt: dtcs.value?.event_datetime,
        reportedCount,
        formatDate: (at) => formatCompanyDateTime(at, DATE_ONLY),
    });
    return {lamps, diagnostics, reportedCount};
});

// Number bubbles on the tabs, same look as the menu counts: {count, severity, label} per tab value
const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;
const tabBadges = computed(() => {
    const badges = {};
    const open = dvirDefects.value.filter(d => d.status !== 'resolved');
    const openCount = Math.max(dvirDefectsTotal.value, open.length);
    if (openCount) {
        const critical = open.filter(d => d.severity === 'critical').length;
        badges[6] = {
            count: openCount, severity: critical ? 'error' : 'warning',
            label: plural(openCount, 'open defect', 'open defects') + (critical ? ` (${critical} critical)` : ''),
        };
    }
    if (pendingInspectionTaskCounts.value) {
        badges[0] = {count: pendingInspectionTaskCounts.value, severity: 'warning',
            label: plural(pendingInspectionTaskCounts.value, 'scheduled inspection pending', 'scheduled inspections pending')};
    }
    if (pendingMaintenanceTaskCounts.value) {
        badges[1] = {count: pendingMaintenanceTaskCounts.value, severity: 'warning',
            label: plural(pendingMaintenanceTaskCounts.value, 'maintenance service pending', 'maintenance services pending')};
    }
    const {lamps, diagnostics, reportedCount} = engineReport.value;
    const bubble = diagnosticBubble({diagnostics, lamps, codeStatus: dtcsStatus.value, reportedCount});
    if (bubble) badges[4] = bubble;
    return badges;
});
const dvirOutOfService = computed(() => dvirDefects.value.some(d => d.severity === 'critical' && d.status !== 'resolved'));

const sevColor = (s) => ({critical: 'error', major: 'warning', minor: 'grey'}[s] || 'grey');
const defectStatusColor = (s) => ({open: 'error', in_repair: 'warning', resolved: 'success'}[s] || 'grey');
const dvirStatusColor = (s) => ({passed: 'success', defects: 'warning', failed: 'error'}[s] || 'grey');
const dvirDriverName = (driver) => {
    const u = driver?.user;
    return u ? `${u.first_name || ''} ${u.last_name || ''}`.trim() || '-' : '-';
};

const fetchDvirInspections = () => {
    dvirProcessing.value = true;
    send('get', 'fleet/inspections', {
        asset: assetID,
        company: asset.value?.company_id ?? null,
        page: dvirInspectionsPage.value,
    })
        .then((response) => {
            const p = response.data.data;
            dvirInspections.value = p?.data ?? [];
            dvirInspectionsTotalPages.value = p?.last_page ?? 1;
        })
        .catch(() => showSnackbar('Inspections could not be loaded', 'error'))
        .finally(() => dvirProcessing.value = false);
};

let dvirDefectsRequest = 0;
const fetchDvirDefects = (silent = false) => {
    const request = ++dvirDefectsRequest;
    send('get', 'fleet/inspection-defects', {
        asset: assetID,
        company: asset.value?.company_id ?? null,
        status: 'open_all',
        per_page: 100,
    })
        .then((response) => {
            if (request !== dvirDefectsRequest) return;
            dvirDefects.value = response.data.data?.data ?? [];
            dvirDefectsTotal.value = response.data.data?.total ?? dvirDefects.value.length;
        })
        .catch(() => {
            if (!silent) showSnackbar('Defects could not be loaded', 'error');
        });
};

const fetchDvir = () => {
    fetchDvirInspections();
    fetchDvirDefects();
};

// A work order adds a maintenance task and resolving a defect can complete its work order.
// The Maintenance tab loads on first open, so only an already loaded list is reloaded.
const refreshAfterDefectChange = () => {
    fetchDvirDefects(true);
    getServiceTaskCounts();
    if (maintenanceTasksCurrentPage.value) fetchMaintenanceTasks();
};

const dvirCreateWorkOrder = (d) => {
    dvirBusy.value = d.id;
    send('post', `fleet/inspection-defects/${d.id}/work-order`)
        .then((response) => {
            d.service_task_id = response.data.data.service_task_id;
            d.status = 'in_repair';
            showSnackbar('Work order created');
            refreshMenuCounts();
            refreshAfterDefectChange();
        })
        .catch((error) => showSnackbar(error.response?.data?.message ?? 'Work order could not be created', 'error'))
        .finally(() => dvirBusy.value = null);
};

const dvirResolveOpen = ref(false);
const dvirResolving = ref([]);
const dvirResolveDefect = (d) => {
    dvirResolving.value = [d];
    dvirResolveOpen.value = true;
};
const onDvirResolved = (doneIds) => {
    dvirDefects.value.filter((d) => doneIds.includes(d.id)).forEach((d) => { d.status = 'resolved'; });
    refreshAfterDefectChange();
};

const dvirPhotoBlobUrl = ref('');
const viewDvirPhoto = (path) => {
    send('get', 'fleet/inspection-photo', {path}, {responseType: 'blob'})
        .then((response) => {
            if (dvirPhotoBlobUrl.value) URL.revokeObjectURL(dvirPhotoBlobUrl.value);
            dvirPhotoBlobUrl.value = URL.createObjectURL(response.data);
            window.open(dvirPhotoBlobUrl.value, '_blank');
        })
        .catch(() => showSnackbar('Photo could not be loaded', 'error'));
};

// Free the last-opened photo blob URL when leaving the page.
onBeforeUnmount(() => {
    if (dvirPhotoBlobUrl.value) URL.revokeObjectURL(dvirPhotoBlobUrl.value);
});

/*********************************
 *** Inspections (DVIR) Tab End ***
 *********************************/

/*******************************
 *** Maintenances Tab Start ***
 *******************************/

const maintenances = ref([]);
const serviceCenters = ref([]);
const inspectionTasksList = ref([]);
const maintenancesProcessing = ref(false);
const maintenanceTasks = ref([]);
const maintenanceTasksTotalPages = ref(1);
const maintenanceTasksCurrentPage = ref(0);

const addServiceCenter = (record) => {
    serviceCenters.value.unshift(record);
}
const fetchServiceCenters = () => {
    send('get', `fleet/service-centers`, {
        list: 1,
        company: asset.value.company_id ?? null,
    }).then((response) => {
        serviceCenters.value = response.data.data.length ? response.data.data : [];
    }).catch((error) => {
        showSnackbar(error.response.data.message ?? 'Service centers could not be loaded', 'error')
    });
}
const fetchInspectionTasksList = () => {
    send('get', `fleet/service-tasks`, {
        type: 1,
        list: 1,
        asset: assetID
    }).then((response) => {
        inspectionTasksList.value = response.data.data.length ? response.data.data : [];
    }).catch((error) => {
        showSnackbar(error.response.data.message ?? 'Inspection tasks list could not be loaded', 'error')
    });
}
const fetchMaintenances = () => {
    if (asset.value.asset_type_id) {
        maintenancesProcessing.value = true;
        send('get', `fleet/service-types`, {
            type: 0,
            list: 1,
        })
            .then((response) => {
                maintenances.value = response.data.data.length ? response.data.data : [];
            })
            .catch((error) => {
                showSnackbar(error.response.data.message ?? 'Maintenances could not be loaded', 'error')
            }).finally(() => maintenancesProcessing.value = false);
    }
}
let maintenanceTasksRequest = 0;
const fetchMaintenanceTasks = () => {
    const request = ++maintenanceTasksRequest;
    maintenancesProcessing.value = true;
    send('get', `fleet/service-tasks`, {
        type: 0,
        asset: assetID,
        page: maintenanceTasksCurrentPage.value
    })
        .then((response) => {
            if (request !== maintenanceTasksRequest) return;
            maintenanceTasks.value = response.data.data.data ?? [];
            maintenanceTasksTotalPages.value = response.data.data.meta.last_page;
            maintenanceTasksCurrentPage.value = response.data.data.meta.current_page;
        })
        .catch((error) => {
            if (request !== maintenanceTasksRequest) return;
            showSnackbar(error.response?.data?.message ?? 'Maintenance tasks could not be loaded', 'error')
        }).finally(() => {
            if (request === maintenanceTasksRequest) maintenancesProcessing.value = false;
        });
}
/*****************************
 *** Maintenances Tab End ***
 *****************************/

/*******************************
 *** Fuel Logs Tab Start ***
 *******************************/

const fuelTypes = ref([]);
const fuelLogs = ref([]);
const selectedFuelLog = ref([]);
const fuelLogsProcessing = ref(false);
const fuelLogsTotalPages = ref(1);
const fuelLogsCurrentPage = ref(0);


const createFuelLogDialog = ref(false)
const showCreateFuelLogDialog = ((log = null) => {
    selectedFuelLog.value = log;
    createFuelLogDialog.value = true;
});

// Only a row that says it has a receipt; a row just added here carries neither key.
// The API says when this role cannot open it; an older API does not say, so the button stays.
const mayHaveReceipt = (row) => !!(row.has_receipt ?? row.receipt_photo) && row.can_view_receipt !== false;

const fetchFuelTypes = () => {
    send('get', `fleet/fuel-types`, {
        list: true,
        company: user.type === 'admin' ? asset.value.company_id : null,
    }).then((response) => {
        fuelTypes.value = response.data.data;
    }).catch((error) => {
        showSnackbar(error.response.data.message ?? "Driver's payment types could not be loaded", 'error')
    });
}
let fuelLogsRequestId = 0;
const fetchFuelLogs = () => {
    const requestId = ++fuelLogsRequestId;
    fuelLogsProcessing.value = true;
    send('get', `fleet/fuel-logs`, {
        type: 0,
        asset: assetID,
        page: fuelLogsCurrentPage.value || 1,
    })
        .then((response) => {
            if (requestId !== fuelLogsRequestId) return;
            fuelLogs.value = response.data.data.data ?? [];
            fuelLogsTotalPages.value = response.data.data.last_page;
            fuelLogsCurrentPage.value = response.data.data.current_page;
        })
        .catch((error) => {
            if (requestId !== fuelLogsRequestId) return;
            showSnackbar(error.response?.data?.message ?? 'Fuel logs could not be loaded', 'error')
        }).finally(() => {
            if (requestId === fuelLogsRequestId) fuelLogsProcessing.value = false;
        });
}
const addFuelLog = (log) => {
    fuelLogs.value.unshift(log);
}
const updateFuelLog = (log) => {
    fuelLogs.value.map((record, index) => {
        if (record.id === log.id) {
            fuelLogs.value[index] = log;
        }
    });
}

/*****************************
 *** Fuel Logs Tab End ***
 *****************************/


/*******************************
 *** Expenses Tab Start ***
 *******************************/

const expenseTypes = ref([]);
const expenses = ref([]);
const selectedExpense = ref([]);
const expensesTotalPages = ref(1);
const expensesCurrentPage = ref(0);
const expenseProcessing = ref(false);

const createExpenseDialog = ref(false);
const expenseDetailsDialog = ref(false);
const showCreateExpenseDialog = ((item = null) => {
    selectedExpense.value = null;
    createExpenseDialog.value = true;
});

const showExpenseDetailsDialog = ((item) => {
    selectedExpense.value = item;
    expenseDetailsDialog.value = true;
});

const fetchExpenseTypes = () => {
    send('get', `fleet/expense-types`, {
        list: true,
        company: user.type === 'admin' ? asset.value.company_id : null,
    }).then((response) => {
        expenseTypes.value = response.data.data;
    }).catch((error) => {
        showSnackbar(error.response.data.message ?? "Expense types could not be loaded", 'error')
    });
}
let expensesRequestId = 0;
const fetchExpenses = () => {
    const requestId = ++expensesRequestId;
    expenseProcessing.value = true;
    send('get', `fleet/expenses`, {
        asset: assetID,
        page: expensesCurrentPage.value || 1,
    }).then((response) => {
        if (requestId !== expensesRequestId) return;
        // This list is a resource collection, so its page numbers sit under meta
        const list = response.data.data;
        expenses.value = list.data ?? [];
        expensesTotalPages.value = list.meta?.last_page ?? list.last_page ?? 1;
        expensesCurrentPage.value = list.meta?.current_page ?? list.current_page ?? 1;
    }).catch((error) => {
        if (requestId !== expensesRequestId) return;
        showSnackbar(error.response?.data?.message ?? 'Expenses could not be loaded', 'error')
    }).finally(() => {
        if (requestId === expensesRequestId) expenseProcessing.value = false;
    });
}
const addExpense = (item) => {
    expenses.value.unshift(item);
}
const updateExpense = (item) => {
    expenses.value.map((record, index) => {
        if (record.id === item.id) {
            expenses.value[index] = item;
        }
    });
}


/*******************************
 *** Expenses Tab End ***
 *******************************/


/*******************************
 *** DTCS Tab Start ***
 *******************************/

const dtcs = ref([]);
const dtcsProcessing = ref(false);
const dtcTypeColor = (type) => {
    const t = type.toLowerCase();
    if (t.includes('stored')) return 'error';
    if (t.includes('pending')) return 'warning';
    if (t.includes('permanent')) return 'deep-purple';
    return 'grey';
};
// loading, ok, none (no codes record yet), denied (role lacks the permission) or error; read by Vehicle Health
const dtcsStatus = ref('');
// A definite answer (codes, none, or no permission) is kept for the visit
const dtcsAnswered = computed(() => ['ok', 'none', 'denied'].includes(dtcsStatus.value));
// The API answers a missing record with 400 "Resource not found!" and a refused role with 400 "Action not permitted!"
const dtcsErrorStatus = (error) => {
    const message = error.response?.data?.message ?? '';
    if (error.response?.status === 404 || /resource not found/i.test(message)) return 'none';
    if (/not permitted/i.test(message)) return 'denied';
    return 'error';
};
// quiet: Vehicle Health shows its own state instead of a toast
const fetchDtcs = (quiet = false) => {
    dtcsProcessing.value = true;
    dtcsStatus.value = 'loading';
    send("get", `assets/${assetID}/dtcs`)
        .then((response) => {
            dtcs.value = response.data.data;
            dtcsStatus.value = 'ok';
        })
        .catch((error) => {
            console.log(error);
            dtcsStatus.value = dtcsErrorStatus(error);
            if (!quiet) showSnackbar("Asset Diagnostic codes could not be loaded", "error");
        }).finally(() => dtcsProcessing.value = false);
}
/*****************************
 *** DTCS Tab End ***
 *****************************/


/*******************************
 *** Trips Tab Start ***
 *******************************/

const trips = ref([]);
const tripsTotalPages = ref(1);
const tripsCurrentPage = ref(1);
const tripsProcessing = ref(false);
const expandedTrip = ref(null);
const tripScoreDialogSwitch = ref(false);
const selectedScoreTrip = ref(null);

// Recap dialog "View events on map" handler - expands the trip's row so
// the embedded TripRouteMap renders, then scrolls it into view. Falls
// back gracefully if the trip is not in the current page of trips.
const onTripScoreViewOnMap = (trip) => {
    if (!trip) return;
    const idx = trips.value.findIndex((t) => t.id === trip.id);
    if (idx >= 0) {
        expandedTrip.value = idx;
        // Wait for the row to render before scrolling.
        setTimeout(() => {
            const el = document.querySelector(`[data-trip-row-id="${trip.id}"]`);
            if (el) el.scrollIntoView({behavior: 'smooth', block: 'center'});
        }, 50);
    }
};

const toggleTripExpand = (index) => {
    expandedTrip.value = expandedTrip.value === index ? null : index;
};

const formatDuration = (totalSeconds) => {
    const hours = Math.floor(totalSeconds / 3600);
    const mins = Math.floor((totalSeconds % 3600) / 60);
    const secs = totalSeconds % 60;
    return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
};

const {tripScoreColor, formatTripScore, hasTripScore} = useTripScore();

const tripDevice = computed(() => ({
    asset_id: assetID,
    company: asset.value?.company_id,
}));


const copyLocation = (trip, type) => {
    switch (type) {
        case 'from':
            if (trip.from && trip.from !== 'Address not found') {
                copyToClipboard(trip.from);
            } else {
                copyToClipboard(trip.start_location['lat'] + ', ' + trip.start_location['lon']);
            }
            break
        case 'to':
            if (trip.to && trip.to !== 'Address not found') {
                copyToClipboard(trip.to);
            } else {
                copyToClipboard(trip.end_location['lat'] + ', ' + trip.end_location['lon']);
            }
            break
    }
}

const updateTripGeocodedAddress = (tripId, address) => {
    if (address['start'] && typeof address['start'] === 'string' && address['end'] && typeof address['end'] === 'string') {
        send("post", `trips/update-location/${tripId}`, address)
            .catch((errorResponse) => console.log('Failed to update trip address:', errorResponse));
    }
};

const geocodeTrip = async (trip, tripIndex) => {
    const location = {start: trip.from, end: trip.to};
    let needsUpdate = false;

    if (!trip.from && trip.start_location) {
        const resolved = await geocodeLatLng(trip.start_location);
        if (isValidAddress(resolved)) {
            location['start'] = resolved;
            needsUpdate = true;
        }
    }
    if (!trip.to && trip.end_location) {
        const resolved = await geocodeLatLng(trip.end_location);
        if (isValidAddress(resolved)) {
            location['end'] = resolved;
            needsUpdate = true;
        }
    }

    if (needsUpdate && tripIndex > -1) {
        if (isValidAddress(location['start'])) trips.value[tripIndex]['from'] = location['start'];
        if (isValidAddress(location['end']))   trips.value[tripIndex]['to']   = location['end'];
        updateTripGeocodedAddress(trip.id, location);
    }
};

const geocodeAllTrips = async () => {
    for (let i = 0; i < trips.value.length; i++) {
        const trip = trips.value[i];
        if (!trip.from || !trip.to) {
            await geocodeTrip(trip, i);
        }
    }
};

const fetchTrips = () => {
    tripsProcessing.value = true;
    expandedTrip.value = null;
    send("get", `assets/${assetID}/trips`, {page: tripsCurrentPage.value})
        .then((response) => {
            trips.value = response.data.data.data;
            tripsTotalPages.value = response.data.data.meta.last_page;
            tripsCurrentPage.value = response.data.data.meta.current_page;
            // Geocode trips that are missing addresses
            geocodeAllTrips();
        })
        .catch((error) => {
            console.log(error);
            showSnackbar("Trips could not be loaded", "error");
        })
        .finally(() => {
            tripsProcessing.value = false
        });
}
/*****************************
 *** Trips Tab End ***
 *****************************/
</script>

<style scoped>
pre {
    color: #ccc;
    padding: 5px;
    background: #2d2d2d;
    white-space: pre-wrap;
}

.v-tab.v-tab.v-btn {
    background-color: #fbfbfb;
    border-right: 1px solid #e7e7e7;
    border-bottom: 1px solid #e7e7e7;
}

.v-tab.v-tab.v-btn.v-tab-item--selected {
    background-color: #F5F5F5 !important;
}

.trip-expanded-content {
    background-color: #fafafa;
    border-top: 1px solid #e0e0e0;
    border-bottom: 1px solid #e0e0e0;
}

.trip-stats-table td {
    padding: 1px 4px !important;
    font-size: 0.8rem;
    height: auto !important;
}

.trip-expanded-content :deep(.half-card) {
    height: 100% !important;
    display: flex;
    flex-direction: column;
}

.trip-expanded-content :deep(.half-card .v-card-title) {
    padding: 4px 8px !important;
}

.trip-expanded-content :deep(.half-card .v-card-title h3) {
    font-size: 0.85rem;
}

.trip-expanded-content :deep(.half-card .v-card-text) {
    height: auto !important;
    flex: 1;
}
</style>
